/**
 * JN-ADMIN-001 (2026-05-27) — Platform admin web client.
 *
 * SEPARATE token storage from the tenant user — different localStorage
 * key (`platform_admin_token`) so a logged-in tenant user can't have
 * their token confused with a platform admin's, and so an admin can
 * still hold a tenant session in another tab without collision.
 *
 * All requests carry the admin token in the Authorization header.
 * On 401 the page redirects to /admin/login.
 */

import type { EmissionFactor, GuidanceBlock, JobStageDef, Nation, PlatformAdminAccess, PlatformAdminSectionKey, SatisfactionQuestion, SupplyType, SupplyUnit } from '@jeanieiq/types';
import { currentScreen } from './clientScreen';
import { describeValidationIssues, fieldInWords } from './validationIssues';
import { foreignReply, newRequestId, readReply, unreachableDetail } from './requestTrace';

const TOKEN_KEY = 'platform_admin_token';

export function getPlatformAdminToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}
export function setPlatformAdminToken(token: string): void {
  try {
    window.localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* swallow */
  }
}
/**
 * WHETHER A FAILED REQUEST MEANS THE ADMIN IS SIGNED OUT (2026-10-03).
 *
 * Only a 401 says that - no token, a bad one, or one that has run out. A 500,
 * a 502 or no answer at all means the server is failing or restarting, and the
 * session is still good: clearing it then signed Shane out of Platform Admin
 * again and again while the dev server restarted under him (proved from his
 * tab: /me -> 500 during a restart, then /admin/login). He asked for this:
 * "yes fix". The pages that send a signed-out admin to sign-in ask this first.
 */
export function isSignedOutError(err: unknown): boolean {
  return err instanceof AdminApiError && err.status === 401;
}

export function clearPlatformAdminToken(): void {
  try {
    window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* swallow */
  }
}

function getAdminApiBase(): string {
  const fromEnv = (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_API_BASE_URL;
  return (fromEnv ?? '').replace(/\/$/, '');
}

async function adminFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getPlatformAdminToken();
  const headers = new Headers(init.headers ?? {});
  headers.set('Content-Type', 'application/json');
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
    // Which admin screen asked, for the Error Log (see lib/clientScreen).
    const screen = currentScreen();
    if (screen) headers.set('X-Client-Screen', screen);
  }
  const url = path.startsWith('http') ? path : `${getAdminApiBase()}${path}`;
  const request = { method: (init.method ?? 'GET').toUpperCase(), path: path.split('?')[0] ?? path };
  // Every admin request is JSON, so preflighted already: the id costs no extra round trip.
  const requestId = newRequestId();
  headers.set('X-Request-Id', requestId);
  const startedAt = Date.now();
  let res: Response;
  try {
    res = await fetch(url, { ...init, headers });
  } catch (err) {
    // An abort is not a failure to connect and passes through untouched.
    if (err instanceof TypeError) {
      throw new AdminApiError('Could not reach the server.', 0, 'NETWORK_ERROR', request, { requestId, detail: unreachableDetail(err, startedAt) });
    }
    throw err;
  }
  const reply = await readReply(res);
  const wrapped = reply.json as { success?: boolean; data?: unknown; error?: { code: string; message: string; details?: unknown } };
  if (!res.ok || wrapped?.success === false) {
    // A refusal says which field and why, not only "Invalid request".
    const headline = wrapped?.error?.message ?? `Request failed (${res.status})`;
    const issues = describeValidationIssues(wrapped?.error?.details, fieldInWords);
    const reachedPlatform = wrapped?.success === false;
    throw new AdminApiError(issues ? `${headline} — ${issues}` : headline, res.status, wrapped?.error?.code ?? null, request, {
      requestId,
      detail: reachedPlatform ? null : foreignReply(res, reply.text),
    });
  }
  return wrapped.data as T;
}

/**
 * What adminFetch throws: still an Error (every existing caller reads only
 * `.message`), plus the server's error `code` and the HTTP status, so a screen
 * can tell BAND_QUOTED from OFFER_EXPIRED without parsing the message
 * (2026-09-24, pricing / show offer plan, Task 10a).
 */
export class AdminApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
    /** Which request failed, for the Error Log; the query string is left off. */
    readonly request: { method: string; path: string } | null = null,
    /** The X-Request-Id it carried, and why it failed when the platform never answered. */
    readonly trace: { requestId: string | null; detail: string | null } = { requestId: null, detail: null },
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

// ---------------------------------------------------------------------------
// First-run setup
// ---------------------------------------------------------------------------

export async function platformAdminGetSetupStatus(): Promise<{ setupRequired: boolean }> {
  const res = await fetch(`${getAdminApiBase()}/api/admin/setup-status`);
  const body = (await res.json()) as { success: boolean; data?: { setupRequired: boolean }; error?: { message: string } };
  if (!res.ok || !body.success) throw new Error(body.error?.message ?? 'Request failed');
  return body.data!;
}

export async function platformAdminSetup(input: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}): Promise<{ id: string; email: string }> {
  const res = await fetch(`${getAdminApiBase()}/api/admin/setup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const body = (await res.json()) as { success: boolean; data?: { id: string; email: string }; error?: { message: string } };
  if (!res.ok || !body.success) throw new Error(body.error?.message ?? 'Setup failed');
  return body.data!;
}

// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------

export interface PlatformAdmin {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  /** JN-ADMIN-MFA-001 — true if admin has enrolled in TOTP MFA. */
  mfaEnabled?: boolean;
  /**
   * TODO 528 — the country this admin is working in: a two-letter code, 'ALL',
   * or null until first chosen. GET /me always carries it; the sign-in reply
   * does not, which is why it is optional here.
   */
  workingCountry?: string | null;
  /**
   * TODO 530 — the permission level, as the server's section check read it.
   * GET /me always carries it; the sign-in reply does not.
   */
  access?: PlatformAdminAccess;
  /** GET /me only - when they last signed in, for Account & security. */
  lastLogin?: string | null;
  /**
   * GET /me only (TODO 535) - the operator company's own time zone:
   * Platform Admin shows a date that belongs to no tenant in it.
   */
  operatorTimeZone?: string;
  /** GET /me only - false until JeanieIQ's own company is set up as the operator. */
  operatorSet?: boolean;
}
export interface PlatformAdminLoginSuccess {
  kind: 'success';
  token: string;
  admin: PlatformAdmin;
}
export interface PlatformAdminLoginMfaRequired {
  kind: 'mfa_required';
  tempToken: string;
}
export type PlatformAdminLoginResult =
  | PlatformAdminLoginSuccess
  | PlatformAdminLoginMfaRequired;

export async function platformAdminLogin(input: {
  email: string;
  password: string;
}): Promise<PlatformAdminLoginResult> {
  return adminFetch<PlatformAdminLoginResult>('/api/admin/login', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
/** JN-ADMIN-MFA-001 — second leg of the MFA login. */
export async function platformAdminLoginMfaVerify(input: {
  tempToken: string;
  code: string;
}): Promise<PlatformAdminLoginSuccess> {
  return adminFetch<PlatformAdminLoginSuccess>('/api/admin/login/mfa/verify', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
/**
 * Self-service "forgot password" (Phase 1, 2026-09-26). Backed by
 * platformAdminService.ts's requestPlatformAdminPasswordReset /
 * completePlatformAdminPasswordReset. adminFetch is NOT used here — both
 * routes are public (no admin token exists yet), and adminFetch always
 * attaches whatever stale token localStorage happens to hold, which is
 * harmless but pointless for a pre-auth call. A plain fetch matches how
 * platformAdminLogin's own pre-auth call is written.
 */
export async function platformAdminRequestPasswordReset(email: string): Promise<void> {
  const res = await fetch(`${getAdminApiBase()}/api/admin/password-reset/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const body = (await res.json().catch(() => null)) as
    | { success?: boolean; error?: { message?: string } }
    | null;
  if (!res.ok || body?.success === false) {
    throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
  }
}

/** Sets a new password with a reset link's token. Every session for the
 *  admin is revoked server-side, this device's included. */
export async function platformAdminCompletePasswordReset(token: string, password: string): Promise<void> {
  const res = await fetch(`${getAdminApiBase()}/api/admin/password-reset/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });
  const body = (await res.json().catch(() => null)) as
    | { success?: boolean; error?: { message?: string } }
    | null;
  if (!res.ok || body?.success === false) {
    throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
  }
}

export async function platformAdminMe(): Promise<PlatformAdmin> {
  return adminFetch<PlatformAdmin>('/api/admin/me');
}

export interface OperatorSetupBody {
  name: string;
  slug: string;
  sector: string;
  countryCode: string;
  timeZone?: string;
  /** The country's region the company is in (Australia: its state or territory); left out, the country's default. */
  region?: string;
  /** YYYY-MM-DD */
  startDate: string;
}
export interface OperatorSetupResult {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  directorEmail: string;
  directorOneTimePassword: string;
  emailSent: boolean;
  employeeId: string | null;
  linkError?: string;
}
/** JeanieIQ's own company, on a platform with no operator yet. */
export async function platformAdminSetUpOperator(body: OperatorSetupBody): Promise<OperatorSetupResult> {
  return adminFetch<OperatorSetupResult>('/api/admin/operator/setup', { method: 'POST', body: JSON.stringify(body) });
}

export interface SignInEmailAvailability {
  email: string;
  available: boolean;
  heldBy: { tenantId: string; tenantName: string; tenantSlug: string } | null;
}

/**
 * Is a sign-in email free, before a tenant is set up with it?
 *
 * Platform admin only, and deliberately so: the answer names the workspace
 * already holding the address, which is what staff need to settle it with the
 * customer and what a customer must never be shown.
 */
export async function platformAdminCheckEmailAvailability(
  email: string,
): Promise<SignInEmailAvailability> {
  return adminFetch<SignInEmailAvailability>(
    `/api/admin/email-availability?email=${encodeURIComponent(email)}`,
  );
}

/** JN-ADMIN-MFA-001 — enrolment endpoints. */
export async function platformAdminMfaSetup(): Promise<{
  secret: string;
  qrCodeUrl: string;
  otpauthUrl: string;
}> {
  return adminFetch('/api/admin/me/mfa/setup', { method: 'POST' });
}
export async function platformAdminMfaEnable(code: string): Promise<{ ok: true }> {
  return adminFetch('/api/admin/me/mfa/enable', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}
export async function platformAdminMfaDisable(input: {
  currentPassword: string;
  code: string;
}): Promise<{ ok: true }> {
  return adminFetch('/api/admin/me/mfa/disable', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ---------------------------------------------------------------------------
// Tenants + users + reset password
// ---------------------------------------------------------------------------

export interface TenantListRow {
  id: string;
  name: string;
  slug: string;
  subscriptionTier: string;
  /** TODO 533 - the tier off the country's price list the tenant is on, if chosen. */
  subscriptionPriceTierId: string | null;
  /** That tier's name, or the line named for its level (Base, Plus); null on Customised. */
  tierName: string | null;
  subscriptionStatus: string;
  countryCode: string;
  /** The tenant's zone - one of its country's, set here in Platform Admin (TODO 257; read by TODO 535). */
  timezone: string;
  /** The region of its country it is in (Australia: its state or territory). */
  region: string;
  isMultiSite: boolean;
  createdAt: string;
  userCount: number;
  employeeCount: number;
  lastUserLogin: string | null;
  /** JN-ADMIN-NOTES-INDICATOR-001 — true when the tenant has any
   *  non-empty internal admin notes. Drives the row indicator. */
  hasNotes: boolean;
  /**
   * IS THIS TENANT ACTUALLY SET UP (2026-08-11)? The standard-library seed on
   * register() is best-effort, so a failure is silent — the tenant exists and
   * its sections are just empty. `missing` names what did not land and what
   * the tenant cannot do because of it.
   */
  provisioning: {
    complete: boolean;
    counts: { qualificationTypes: number; nominalCodes: number };
    missing: Array<{ key: string; label: string; consequence: string }>;
  };
  /** Where the tenant stands — drives the countdown on the detail page. */
  trialEndsAt: string | null;
  /** 'monthly' | 'annual' — per tenant, so renewals fall on different days. */
  subscriptionPeriod: string | null;
  subscriptionRenewsAt: string | null;
  permanentReason: string;
  trialExtensions: number;
  /** What a suspended tenant returns to when reactivated. */
  statusBeforeSuspension: string | null;
  /** The agreed deal, a monthly figure ex VAT. Null until set. */
  customisedPrice: number | null;
  customisedPriceNotes: string;
  /** The show offer code held, if any. */
  offerCode: string | null;
  /** Whether that offer also comes off the agreed price. */
  offerAppliesToDeal: boolean;
  /** On a 12-month term. "Start subscription" is never offered then. */
  hasTerm: boolean;
  sector: string;
  /** The add-on keys the tenant holds. */
  addonKeys: string[];
}

export async function platformAdminSetTenantSuspension(input: {
  tenantId: string;
  action: 'suspend' | 'activate';
  reason?: string | null;
}): Promise<{ tenantId: string; newStatus: string }> {
  return adminFetch(`/api/admin/tenants/${input.tenantId}/suspension`, {
    method: 'POST',
    body: JSON.stringify({ action: input.action, reason: input.reason ?? null }),
  });
}
/*
 * THE SUBSCRIPTION LIFECYCLE (Shane, 2026-08-09). Three acts, each its own
 * call, because they are different decisions: extending a trial is not the
 * same as putting somebody on free, and the audit has to say which happened.
 */

/** Push ONE tenant's trial out. The Trials default in Platform settings is
 *  for tenants not yet created and is untouched by this. */
export async function platformAdminExtendTrial(input: {
  tenantId: string;
  days: number;
  reason?: string | null;
}): Promise<{ tenantId: string; trialEndsAt: string; extensions: number }> {
  return adminFetch(`/api/admin/tenants/${input.tenantId}/trial/extend`, {
    method: 'POST',
    body: JSON.stringify({ days: input.days, reason: input.reason ?? null }),
  });
}

/** Trial -> paying subscription, priced as the New customer card prices one. */
export interface StartSubscriptionBody {
  headcount: number | null;
  dealPounds?: number | null;
  dealNotes?: string;
  addonKeys: string[];
  offerCode?: string;
  freeDays?: number | null;
  freeDaysConfirmedBy?: string;
}
export async function platformAdminStartSubscription(
  tenantId: string,
  body: StartSubscriptionBody,
): Promise<{ tenantId: string; freeDays: number; firstPaidDate: string; linkSentTo: string | null; linkError?: string }> {
  return adminFetch(`/api/admin/tenants/${encodeURIComponent(tenantId)}/subscription`, { method: 'POST', body: JSON.stringify(body) });
}

/** Free forever, granted deliberately. The reason is required. */
export async function platformAdminMakePermanent(input: {
  tenantId: string;
  reason: string;
}): Promise<{ tenantId: string }> {
  return adminFetch(`/api/admin/tenants/${input.tenantId}/permanent`, {
    method: 'POST',
    body: JSON.stringify({ reason: input.reason }),
  });
}

/** Stop a permanent grant — back to trial with no end date, so they keep
 *  working and a subscription can be started. Reason required. */
export async function platformAdminEndPermanent(input: {
  tenantId: string;
  reason: string;
}): Promise<{ tenantId: string; newStatus: string }> {
  return adminFetch(`/api/admin/tenants/${input.tenantId}/permanent`, {
    method: 'DELETE',
    body: JSON.stringify({ reason: input.reason }),
  });
}

/** The agreed deal: a monthly figure ex VAT, for any tenant. null clears it. */
export async function platformAdminSetCustomisedPrice(input: {
  tenantId: string;
  price: number | null;
  notes?: string | null;
}): Promise<{ tenantId: string; customisedPrice: number | null }> {
  return adminFetch(`/api/admin/tenants/${input.tenantId}/customised-price`, {
    method: 'PUT',
    body: JSON.stringify({ price: input.price, notes: input.notes ?? null }),
  });
}

/** Whether a show offer also comes off this tenant's agreed price. */
export async function platformAdminSetOfferAppliesToDeal(input: {
  tenantId: string;
  applies: boolean;
}): Promise<{ tenantId: string; offerAppliesToDeal: boolean }> {
  return adminFetch(`/api/admin/tenants/${input.tenantId}/offer-applies-to-deal`, {
    method: 'PUT',
    body: JSON.stringify({ applies: input.applies }),
  });
}

/** Bring every live tenant into the operator's CRM as a customer. Idempotent —
 *  running it twice changes nothing. */
export async function platformAdminSyncOperatorCustomers(): Promise<{
  operatorTenantId: string | null;
  created: number;
  renamed: number;
  /** Already there, with empty details filled in from the tenant. */
  filled: number;
  unchanged: number;
}> {
  return adminFetch('/api/admin/operator/sync-customers', { method: 'POST' });
}

/** Raise this tenant's subscription invoice in the operator's Accounts. */
export async function platformAdminRaiseSubscriptionInvoice(
  tenantId: string,
): Promise<{ invoiceId: string; reference: string; amount: number; periodEnd: string }> {
  return adminFetch(`/api/admin/tenants/${tenantId}/subscription-invoice`, { method: 'POST' });
}

/**
 * The tenants in the working country (TODO 528) - the server reads the country
 * off the admin's account, so this sends none.
 */
export async function platformAdminListTenants(): Promise<TenantListRow[]> {
  const data = await adminFetch<{ tenants: TenantListRow[] }>('/api/admin/tenants');
  return data.tenants;
}

/**
 * The directory waiting list: companies that asked to advertise to other
 * JeanieIQ tenants. Handled rows stay on it, because "have we already been
 * back to them" is the question the list exists to answer.
 */
export interface DirectoryInterestRow {
  tenantId: string;
  name: string;
  slug: string;
  registeredAt: string;
  handledAt: string | null;
  handledBy: string | null;
  /** Null when the company has no live director to contact. */
  contactEmail: string | null;
}

export async function platformAdminListDirectoryInterest(): Promise<DirectoryInterestRow[]> {
  const data = await adminFetch<{ companies: DirectoryInterestRow[] }>('/api/admin/directory-interest');
  return data.companies;
}

export async function platformAdminSetDirectoryInterestHandled(
  tenantId: string,
  handled: boolean,
): Promise<DirectoryInterestRow> {
  return adminFetch<DirectoryInterestRow>(`/api/admin/directory-interest/${tenantId}/handled`, {
    method: 'POST',
    body: JSON.stringify({ handled }),
  });
}

/**
 * Every tenant, whatever the working country - only for the platform-wide
 * settings that pick from all of them (Settings' Operator card).
 */
export async function platformAdminListTenantsInEveryCountry(): Promise<TenantListRow[]> {
  const data = await adminFetch<{ tenants: TenantListRow[] }>('/api/admin/tenants?allCountries=true');
  return data.tenants;
}

/** One tenant, whatever the working country, so it always opens (TODO 528). */
export async function platformAdminGetTenant(id: string): Promise<TenantListRow> {
  const data = await adminFetch<{ tenant: TenantListRow }>(`/api/admin/tenants/${encodeURIComponent(id)}`);
  return data.tenant;
}

/** Remember the country this admin is working in: a listed country, or ALL. */
export async function platformAdminSetWorkingCountry(country: string): Promise<{ workingCountry: string }> {
  return adminFetch<{ workingCountry: string }>('/api/admin/me/working-country', {
    method: 'PUT',
    body: JSON.stringify({ country }),
  });
}

export interface TenantUserListRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  lastLogin: string | null;
}
export async function platformAdminListUsers(
  tenantId: string,
): Promise<TenantUserListRow[]> {
  const data = await adminFetch<{ users: TenantUserListRow[] }>(
    `/api/admin/tenants/${tenantId}/users`,
  );
  return data.users;
}

/** JN-ADMIN-REVOKE-001 — force sign-out (revoke refresh tokens). */
export async function platformAdminRevokeUserSessions(
  userId: string,
): Promise<{ userId: string; revokedCount: number }> {
  return adminFetch(`/api/admin/users/${userId}/revoke-sessions`, {
    method: 'POST',
  });
}
export async function platformAdminRevokeTenantSessions(input: {
  tenantId: string;
  confirm: string;
}): Promise<{ tenantId: string; revokedCount: number; userCount: number }> {
  return adminFetch(`/api/admin/tenants/${input.tenantId}/revoke-sessions`, {
    method: 'POST',
    body: JSON.stringify({ confirm: input.confirm }),
  });
}

/** JN-ADMIN-USERINVITE-001 — resend invite for any user. */
export interface ResendUserInviteResult {
  userId: string;
  tenantId: string;
  tenantName: string;
  userEmail: string;
  userRole: string;
  newPassword: string;
  emailSent: boolean;
  emailError?: string;
}
export async function platformAdminResendUserInvite(
  userId: string,
): Promise<ResendUserInviteResult> {
  return adminFetch<ResendUserInviteResult>(
    `/api/admin/users/${userId}/resend-invite`,
    { method: 'POST' },
  );
}

/** JN-ADMIN-USEREDIT-001 — correct a tenant user's name / email / role. */
export interface UpdateTenantUserPayload {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: 'director' | 'manager' | 'employee';
}
export async function platformAdminUpdateTenantUser(
  userId: string,
  patch: UpdateTenantUserPayload,
): Promise<{ userId: string; changedFields: string[] }> {
  return adminFetch(`/api/admin/users/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/** JN-ADMIN-USERSTATE-001 — soft-disable / re-enable a tenant user. */
export async function platformAdminSetUserActive(input: {
  userId: string;
  isActive: boolean;
}): Promise<{ userId: string; isActive: boolean; alreadyInState: boolean }> {
  return adminFetch(`/api/admin/users/${input.userId}/active`, {
    method: 'POST',
    body: JSON.stringify({ isActive: input.isActive }),
  });
}

export async function platformAdminResetPassword(
  userId: string,
): Promise<{
  newPassword: string;
  targetTenantId: string;
  emailSent: boolean;
  emailError?: string;
}> {
  return adminFetch<{
    newPassword: string;
    targetTenantId: string;
    emailSent: boolean;
    emailError?: string;
  }>(`/api/admin/users/${userId}/reset-password`, { method: 'POST' });
}

/** JN-ADMIN-001 Phase 2.5 — admin changes own password. */
export async function platformAdminChangeOwnPassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ ok: true }> {
  return adminFetch<{ ok: true }>('/api/admin/me/change-password', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** JN-ADMIN-001 Phase 2.5 — list + add platform admins. */
export interface PlatformAdminListRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  lastLogin: string | null;
  createdAt: string;
  isSelf: boolean;
  /** TODO 530 — the permission level this admin is on. */
  levelId: string;
  levelName: string;
  /** TODO 530b — the operator employee this admin is, or null until linked. */
  employeeId: string | null;
  employeeName: string | null;
}
export async function platformAdminListAdmins(): Promise<PlatformAdminListRow[]> {
  const data = await adminFetch<{ admins: PlatformAdminListRow[] }>(
    '/api/admin/admins',
  );
  return data.admins;
}
/**
 * Add admin (TODO 530b): the person is one of the operator company's employees -
 * their name and sign-in address come from HR - and the level is chosen.
 */
export async function platformAdminCreateAdmin(input: {
  employeeId: string;
  /** TODO 530 — the permission level they start on. */
  levelId: string;
}): Promise<{
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  oneTimePassword: string;
  emailSent: boolean;
  emailError?: string;
}> {
  return adminFetch('/api/admin/admins', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/*
 * PERMISSION LEVELS (TODO 530). A name and the Platform Admin sections it
 * opens; every admin is on one. Full access is built in and fixed.
 */
export interface AdminLevel {
  id: string;
  name: string;
  fullAccess: boolean;
  sections: PlatformAdminSectionKey[];
  /** Every country, or only `countries`. Full access: always every country. */
  allCountries: boolean;
  countries: string[];
  adminCount: number;
}
export async function platformAdminListLevels(): Promise<AdminLevel[]> {
  const data = await adminFetch<{ levels: AdminLevel[] }>('/api/admin/admin-levels');
  return data.levels;
}
export interface AdminLevelInput {
  name: string;
  sections: PlatformAdminSectionKey[];
  allCountries: boolean;
  countries: string[];
}
export async function platformAdminCreateLevel(input: AdminLevelInput): Promise<{ id: string }> {
  return adminFetch('/api/admin/admin-levels', { method: 'POST', body: JSON.stringify(input) });
}
export async function platformAdminUpdateLevel(id: string, input: Partial<AdminLevelInput>): Promise<{ ok: true }> {
  return adminFetch(`/api/admin/admin-levels/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) });
}
export async function platformAdminDeleteLevel(id: string): Promise<{ ok: true }> {
  return adminFetch(`/api/admin/admin-levels/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export async function platformAdminSetAdminLevel(adminId: string, levelId: string): Promise<{ ok: true }> {
  return adminFetch(`/api/admin/admins/${encodeURIComponent(adminId)}/level`, { method: 'PUT', body: JSON.stringify({ levelId }) });
}

/** The operator company's current employees, for Add admin and Link to employee (TODO 530b). */
export interface OperatorEmployee {
  id: string;
  name: string;
  email: string | null;
  /** The admin already linked to this person, if any. */
  adminId: string | null;
}
export async function platformAdminListOperatorEmployees(): Promise<{ operatorSet: boolean; employees: OperatorEmployee[] }> {
  return adminFetch('/api/admin/operator-employees');
}
export async function platformAdminLinkAdminEmployee(adminId: string, employeeId: string): Promise<{ ok: true }> {
  return adminFetch(`/api/admin/admins/${encodeURIComponent(adminId)}/employee`, { method: 'PUT', body: JSON.stringify({ employeeId }) });
}

export async function platformAdminDeleteAdmin(
  id: string,
): Promise<{ ok: true }> {
  return adminFetch<{ ok: true }>(`/api/admin/admins/${id}`, {
    method: 'DELETE',
  });
}

export async function platformAdminSetAdminActive(
  id: string,
  isActive: boolean,
): Promise<{ ok: true }> {
  return adminFetch<{ ok: true }>(`/api/admin/admins/${id}/active`, {
    method: 'POST',
    body: JSON.stringify({ isActive }),
  });
}

export async function platformAdminResetAdminPassword(
  id: string,
): Promise<{ newPassword: string }> {
  return adminFetch<{ newPassword: string }>(`/api/admin/admins/${id}/reset-password`, {
    method: 'POST',
  });
}

/** JN-ADMIN-002 — Create new tenant (with primary Site + first director). */
export interface CreateTenantPayload {
  name: string;
  slug: string;
  sector: string;
  countryCode?: string;
  /** TODO 257 - one of the country's own time zones; left out, its main zone. */
  timeZone?: string;
  /** The country's region the company is in (Australia: its state or territory); left out, the country's default. */
  region?: string;
  subscriptionTier?: 'base' | 'plus' | 'customised';
  /** TODO 533 - a tier off the country's price list; sets the level it behaves as. */
  subscriptionPriceTierId?: string;
  directorEmail: string;
  directorFirstName: string;
  directorLastName: string;
  primarySiteName?: string;
}
export interface CreateTenantResult {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  directorEmail: string;
  directorOneTimePassword: string;
  emailSent: boolean;
  emailError?: string;
}
export async function platformAdminCreateTenant(
  input: CreateTenantPayload,
): Promise<CreateTenantResult> {
  return adminFetch<CreateTenantResult>('/api/admin/tenants', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** JN-ADMIN-RESEND-001 — resend director invite. */
export interface ResendInviteResult {
  tenantId: string;
  tenantName: string;
  directorUserId: string;
  directorEmail: string;
  directorOneTimePassword: string;
  emailSent: boolean;
  emailError?: string;
}
export async function platformAdminResendDirectorInvite(input: {
  tenantId: string;
  targetUserId?: string;
}): Promise<ResendInviteResult> {
  return adminFetch<ResendInviteResult>(
    `/api/admin/tenants/${input.tenantId}/resend-invite`,
    {
      method: 'POST',
      body: JSON.stringify(input.targetUserId ? { targetUserId: input.targetUserId } : {}),
    },
  );
}

/** JN-ADMIN-EDIT-001 — partial-update an existing tenant. */
export interface UpdateTenantPayload {
  name?: string;
  sector?: string;
  countryCode?: string;
  /** TODO 257 - one of the zones of the country the tenant is, or is moving to, in. */
  timeZone?: string;
  /** The country's region the company is in (Australia: its state or territory); left out, the country's default. */
  region?: string;
  subscriptionTier?: 'base' | 'plus' | 'customised';
  /** TODO 533 - a tier off the country's price list, or null to come off one. */
  subscriptionPriceTierId?: string | null;
}
export async function platformAdminUpdateTenant(
  tenantId: string,
  patch: UpdateTenantPayload,
): Promise<{ tenantId: string; changedFields: string[] }> {
  return adminFetch(`/api/admin/tenants/${tenantId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/*
 * SETTINGS > TIME ZONE (TODO 257, 2026-10-10). Each country has one MAIN time
 * zone and may have sub zones. A tenant is on its country's main zone unless a
 * sub zone is picked for it when it is set up; the tenant does not set its own.
 */

/** One zone of a country: the row, its clock right now, and who is on it. */
export interface CountryTimeZone {
  id: string;
  /** IANA name, e.g. "Australia/Perth". */
  timeZone: string;
  /** What a person calls it, e.g. "Western Australia". May be empty. */
  label: string;
  isMain: boolean;
  /** Tenants of this country on this zone. */
  tenants: number;
  /** The zone's own short name now, as its country says it ("AEDT"); may be empty. */
  abbreviationNow: string;
  /** The zone's full name now ("Australian Eastern Daylight Time"); may be empty. */
  nameNow: string;
  offsetMinutesNow: number;
  daylightSavingNow: boolean;
  /** `yyyy-mm-dd HH:MM` in that zone, now. */
  localTimeNow: string;
}

export interface CountryTimeZones {
  countryCode: string;
  countryName: string;
  /** The main zone first. Empty for a country whose time has not been set. */
  zones: CountryTimeZone[];
  /** Tenants of this country on a zone that is not on its list. */
  offList: Array<{ timeZone: string; tenants: number }>;
  /** The zone each region keeps (an Australian state): a company there with no zone picked goes on it. */
  regionZones: Record<string, string>;
}

/** Every country the admin's level works in, with its zones. */
export async function platformAdminListTimeZones(): Promise<CountryTimeZones[]> {
  return adminFetch('/api/admin/time-zones');
}

/** Every zone name that can be added to a country: places, never an offset. */
export async function platformAdminTimeZoneOptions(): Promise<string[]> {
  return adminFetch('/api/admin/time-zone-options');
}

/** One country's zones - what the New tenant and Edit tenant forms pick from. */
export async function platformAdminCountryTimeZones(countryCode: string): Promise<CountryTimeZones> {
  return adminFetch(`/api/admin/country/${encodeURIComponent(countryCode)}/time-zones`);
}

export async function platformAdminAddTimeZone(
  countryCode: string,
  input: { timeZone: string; label?: string },
): Promise<{ id: string; timeZone: string; isMain: boolean }> {
  return adminFetch(`/api/admin/country/${encodeURIComponent(countryCode)}/time-zones`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function platformAdminRenameTimeZone(
  countryCode: string,
  id: string,
  label: string,
): Promise<{ id: string; label: string }> {
  return adminFetch(`/api/admin/country/${encodeURIComponent(countryCode)}/time-zones/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ label }),
  });
}

/** Makes it the country's main zone. The tenants on the old main zone follow. */
export async function platformAdminMakeMainTimeZone(
  countryCode: string,
  id: string,
): Promise<{ id: string; timeZone: string; tenantsMoved: number }> {
  return adminFetch(`/api/admin/country/${encodeURIComponent(countryCode)}/time-zones/${id}/main`, {
    method: 'PUT',
  });
}

export async function platformAdminRemoveTimeZone(countryCode: string, id: string): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${encodeURIComponent(countryCode)}/time-zones/${id}`, {
    method: 'DELETE',
  });
}

/** JN-ADMIN-IMP-001 — Sudo-as / impersonate a tenant user. */
export interface ImpersonationResult {
  accessToken: string;
  expiresIn: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  tenant: {
    id: string;
    name: string;
    slug: string;
  };
}
export async function platformAdminImpersonateUser(
  userId: string,
): Promise<ImpersonationResult> {
  return adminFetch<ImpersonationResult>(
    `/api/admin/users/${userId}/impersonate`,
    { method: 'POST' },
  );
}
export async function platformAdminEndImpersonation(input: {
  targetUserId: string;
  targetTenantId: string;
}): Promise<{ ok: true }> {
  return adminFetch<{ ok: true }>('/api/admin/impersonation/end', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** JN-ADMIN-BULK-001 — bulk action across a set of tenants. */
export type BulkTenantAction =
  | { kind: 'suspend'; reason?: string | null }
  | { kind: 'activate' }
  | { kind: 'setFeatureFlag'; flag: TenantFeatureFlagKey; value: boolean };
export interface BulkTenantResult {
  results: { tenantId: string; ok: boolean; message?: string }[];
  succeeded: number;
  failed: number;
}
export async function platformAdminBulkTenantAction(input: {
  tenantIds: string[];
  action: BulkTenantAction;
}): Promise<BulkTenantResult> {
  return adminFetch('/api/admin/tenants/bulk', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** JN-ADMIN-DELETE-001 — soft-delete / restore / trash. */
export interface DeletedTenantRow {
  id: string;
  name: string;
  slug: string;
  subscriptionTier: string;
  deletedAt: string;
  daysSinceDeleted: number;
}
export async function platformAdminListDeletedTenants(): Promise<
  DeletedTenantRow[]
> {
  const data = await adminFetch<{ tenants: DeletedTenantRow[] }>(
    '/api/admin/tenants-deleted',
  );
  return data.tenants;
}
export async function platformAdminSoftDeleteTenant(input: {
  tenantId: string;
  confirm: string;
  reason?: string | null;
}): Promise<{ tenantId: string; deletedAt: string }> {
  return adminFetch(`/api/admin/tenants/${input.tenantId}`, {
    method: 'DELETE',
    body: JSON.stringify({ confirm: input.confirm, reason: input.reason ?? null }),
  });
}
export async function platformAdminRestoreTenant(
  tenantId: string,
): Promise<{ tenantId: string }> {
  return adminFetch(`/api/admin/tenants/${tenantId}/restore`, {
    method: 'POST',
  });
}

/**
 * FINISH SETUP — re-run the standard-library seed for a tenant whose
 * best-effort seed failed on register() (Shane, 2026-08-13).
 *
 * The SETUP INCOMPLETE badge has flagged this since 2026-08-11, but the only
 * cure was a script on the API host, which is no use for a tenant living on
 * the server. Idempotent: it creates only what is missing, so it is safe to
 * press on a healthy tenant and safe to press twice.
 */
export interface FinishTenantSetupResult {
  tenantId: string;
  tenantSlug: string;
  /** Zero everywhere means it was already complete and nothing was needed. */
  wrote: {
    policies: number;
    memos: number;
    nominalCodes: number;
    standardLibrary: Array<{ model: string; created: number }>;
  };
  provisioning: {
    complete: boolean;
    counts: { qualificationTypes: number; nominalCodes: number };
    missing: Array<{ key: string; label: string; consequence: string }>;
  };
}
export async function platformAdminFinishTenantSetup(
  tenantId: string,
): Promise<FinishTenantSetupResult> {
  return adminFetch(`/api/admin/tenants/${tenantId}/finish-setup`, {
    method: 'POST',
  });
}

// 2026-06-28 — Country Configuration content editing (Shane + Matt). Pilot:
// Glossary. Edits the platform-level country-scoped glossary that feeds tenants.
/**
 * A library document's status (2026-10-14), the GDPR Vault's three values on
 * every Country Configuration library. A label for JeanieIQ admin: what a
 * company is offered does not read it.
 */
export type LibraryStatus = 'draft' | 'in_review' | 'approved';

export interface CountryGlossaryEntry {
  id: string;
  /** The library version: 1 when created, one more each time a save changes it (2026-10-14). */
  version: number;
  /** Draft, In review or Approved - chosen in the edit form, shown on the list (2026-10-14). */
  libraryStatus: LibraryStatus;
  term: string;
  abbreviation: string | null;
  definition: string;
  preview: string;
  category: string;
  legalSource: string | null;
  officialSourceUrl: string | null;
  officialSourceLabel: string | null;
  guidanceArea: string | null;
  relatedModules: string[];
  /**
   * Other glossary entries shown as "Related terms", as their real ids.
   * The server has always returned this; nothing on the client had a name
   * for it, which is part of why the editor had no field for it and the
   * only entries carrying related terms were the 117 from the bootstrap.
   */
  relatedTermIds: string[];
  countryCodes: string[];
  /** Applies to: England, Wales or both. Always returned; the editor did not read it until 2026-10-14. */
  nations: Nation[];
  relevantSectors: string[];
  relevantSections: string[];
  lastReviewedAt: string;
}
export interface CountryGlossaryInput {
  term: string;
  abbreviation?: string | null;
  definition: string;
  preview: string;
  category: string;
  legalSource?: string | null;
  officialSourceUrl?: string | null;
  officialSourceLabel?: string | null;
  guidanceArea?: string | null;
  relatedModules?: string[];
  /** Devolved-nation targeting. Omitted = UK-wide, matching the column default. */
  nations?: Nation[];
  relevantSectors?: string[];
  /** Platform sections this term belongs to; ['all'] = every section. */
  relevantSections?: string[];
  /**
   * Related entries, as ids. Omit to leave them as they are — the service
   * reads an absent field as "no opinion", so a caller that predates this
   * cannot wipe them. The route caps it at 12 and the service refuses any
   * id that does not name a live entry.
   */
  relatedTermIds?: string[];
  /** Read on update only: a new document starts as a draft. */
  libraryStatus?: LibraryStatus;
}
export async function platformAdminListCountryGlossary(
  countryCode: string,
): Promise<CountryGlossaryEntry[]> {
  return adminFetch(`/api/admin/country/${countryCode}/glossary`);
}
export async function platformAdminCreateCountryGlossary(
  countryCode: string,
  input: CountryGlossaryInput,
): Promise<{ id: string; term: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/glossary`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function platformAdminUpdateCountryGlossary(
  countryCode: string,
  id: string,
  input: CountryGlossaryInput,
): Promise<{ id: string; term: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/glossary/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}
export async function platformAdminDeleteCountryGlossary(
  countryCode: string,
  id: string,
): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/glossary/${id}`, {
    method: 'DELETE',
  });
}

// 2026-06-28 — Country Configuration → Guidance (area + content).
export interface CountryGuidanceContentRow {
  whyItMatters: string;
  keyPoints: string[];
  platformHandles: string | null;
  warnings: string[];
  relatedAreaSlugs: string[];
  sourceTags: string[];
  bodyHtml: string | null;
  /** spec-45 — the block tree the body was composed from. NULL for areas
   *  authored before the engine; those still serve their stored HTML. */
  bodyBlocks: GuidanceBlock[] | null;
}
export interface SidebarSectionRef {
  key: string;
  label: string;
  group: string;
}
/** The sidebar section catalogue — content is allocated to these. */
export async function platformAdminListSections(): Promise<SidebarSectionRef[]> {
  const d = await adminFetch<{ sections: SidebarSectionRef[] }>('/api/admin/sections');
  return d.sections;
}

export interface CountryGuidanceArea {
  id: string;
  /** The library version: 1 when created, one more each time a save changes it (2026-10-14). */
  version: number;
  /** Draft, In review or Approved - chosen in the edit form, shown on the list (2026-10-14). */
  libraryStatus: LibraryStatus;
  /** Applies to: England, Wales or both (2026-10-14). */
  nations: Nation[];
  areaNumber: number;
  slug: string;
  title: string;
  subtitle: string;
  groupSlug: string;
  groupOrder: number;
  groupLabel: string;
  relevanceVariant: string;
  tags: string[];
  isWizardTrigger: boolean;
  wizardTriggerKey: string | null;
  isNew: boolean;
  relevantSectors: string[];
  relevantSections: string[];
  content: CountryGuidanceContentRow[];
}
export interface CountryGuidanceInput {
  areaNumber?: number;
  slug: string;
  title: string;
  subtitle: string;
  groupSlug: string;
  groupOrder: number;
  groupLabel: string;
  relevanceVariant?: string;
  tags?: string[];
  isWizardTrigger?: boolean;
  wizardTriggerKey?: string | null;
  isNew?: boolean;
  whyItMatters: string;
  keyPoints?: string[];
  platformHandles?: string | null;
  warnings?: string[];
  relatedAreaSlugs?: string[];
  sourceTags?: string[];
  bodyHtml?: string | null;
  /** spec-45 — when supplied these are the source of truth: the server
   *  validates the structure and RENDERS bodyHtml from them, ignoring any
   *  bodyHtml sent alongside. */
  bodyBlocks?: GuidanceBlock[] | null;
  relevantSectors?: string[];
  /** Platform sections this area belongs to; ['all'] = every section. */
  relevantSections?: string[];
  /** Read on update only: a new document starts as a draft. */
  libraryStatus?: LibraryStatus;
  /** Not sent keeps it; never empty. */
  nations?: Nation[];
}
export async function platformAdminListCountryGuidance(
  countryCode: string,
): Promise<CountryGuidanceArea[]> {
  return adminFetch(`/api/admin/country/${countryCode}/guidance`);
}
export async function platformAdminCreateCountryGuidance(
  countryCode: string,
  input: CountryGuidanceInput,
): Promise<{ id: string; title: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/guidance`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function platformAdminUpdateCountryGuidance(
  countryCode: string,
  id: string,
  input: CountryGuidanceInput,
): Promise<{ id: string; title: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/guidance/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}
export async function platformAdminDeleteCountryGuidance(
  countryCode: string,
  id: string,
): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/guidance/${id}`, {
    method: 'DELETE',
  });
}

// 2026-07-27 — Country Configuration → Guidance GROUPS: the library's front
// cards (heading + description + order). Previously a hardcoded constant.
export interface CountryGuidanceGroup {
  id: string;
  slug: string;
  label: string;
  description: string;
  sortOrder: number;
  isActive: boolean;
  /** Live guidance areas sitting under this group. */
  areaCount: number;
}
export interface CountryGuidanceGroupInput {
  /** Create only — immutable afterwards (areas store it as their group_slug). */
  slug?: string;
  label: string;
  description?: string;
  sortOrder?: number;
  isActive?: boolean;
}
export async function platformAdminListCountryGuidanceGroups(
  countryCode: string,
): Promise<CountryGuidanceGroup[]> {
  return adminFetch(`/api/admin/country/${countryCode}/guidance-groups`);
}
export async function platformAdminCreateCountryGuidanceGroup(
  countryCode: string,
  input: CountryGuidanceGroupInput,
): Promise<{ id: string; slug: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/guidance-groups`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function platformAdminUpdateCountryGuidanceGroup(
  countryCode: string,
  id: string,
  input: CountryGuidanceGroupInput,
): Promise<{ id: string; slug: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/guidance-groups/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}
export async function platformAdminDeleteCountryGuidanceGroup(
  countryCode: string,
  id: string,
): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/guidance-groups/${id}`, {
    method: 'DELETE',
  });
}

// 2026-06-29 — Country Configuration → Policies (the editable base TEMPLATE
// that seeds each tenant their own copy; base-then-own).
export interface CountryPolicyTemplate {
  id: string;
  /** The library version: 1 when created, one more each time a save changes it (2026-10-14). */
  version: number;
  /** Draft, In review or Approved - chosen in the edit form, shown on the list (2026-10-14). */
  libraryStatus: LibraryStatus;
  /** Applies to: England, Wales or both (2026-10-14). */
  nations: Nation[];
  policyNumber: number;
  name: string;
  legalBasis: string;
  category: string;
  content: string | null;
  tags: string[];
  relevantSectors: string[];
  enforcementLevel: number;
  gracePeriodDays: number;
  renewalMonths: number | null;
}
export interface CountryPolicyInput {
  name: string;
  legalBasis: string;
  category: string;
  content?: string | null;
  tags?: string[];
  relevantSectors?: string[];
  enforcementLevel?: number;
  gracePeriodDays?: number;
  renewalMonths?: number | null;
  /** Read on update only: a new document starts as a draft. */
  libraryStatus?: LibraryStatus;
  /** Not sent keeps it; never empty. */
  nations?: Nation[];
}
export async function platformAdminListCountryPolicies(
  countryCode: string,
): Promise<CountryPolicyTemplate[]> {
  return adminFetch(`/api/admin/country/${countryCode}/policies`);
}
export async function platformAdminCreateCountryPolicy(
  countryCode: string,
  input: CountryPolicyInput,
): Promise<{ id: string; name: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/policies`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function platformAdminUpdateCountryPolicy(
  countryCode: string,
  id: string,
  input: CountryPolicyInput,
): Promise<{ id: string; name: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/policies/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}
export async function platformAdminDeleteCountryPolicy(
  countryCode: string,
  id: string,
): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/policies/${id}`, {
    method: 'DELETE',
  });
}

// 2026-10-13 — Country Configuration → Categories. One list per country that
// every content area files under - Policies, Memos, Toolbox Talks, Training, Fleet Toolbox, RAMS, the GDPR
// Vault and Contracts & Legals. The key is made from
// the name when a category is added and never changes.
export interface CountryCategory {
  id: string;
  categoryKey: string;
  label: string;
  sortOrder: number;
  /** The library's own documents filed under it, area by area. */
  inLibrary: {
    policies: number;
    memos: number;
    talks: number;
    courses: number;
    fleetTalks: number;
    rams: number;
    vault: number;
    pharmacyDocs: number;
    contracts: number;
  };
}
export async function platformAdminListCountryCategories(countryCode: string): Promise<CountryCategory[]> {
  return adminFetch(`/api/admin/country/${countryCode}/categories`);
}
export async function platformAdminCreateCountryCategory(
  countryCode: string,
  label: string,
): Promise<{ id: string; categoryKey: string; label: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/categories`, {
    method: 'POST',
    body: JSON.stringify({ label }),
  });
}
export async function platformAdminUpdateCountryCategory(
  countryCode: string,
  id: string,
  input: { label?: string; sortOrder?: number },
): Promise<{ id: string; categoryKey: string; label: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}
export async function platformAdminDeleteCountryCategory(countryCode: string, id: string): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/categories/${id}`, {
    method: 'DELETE',
  });
}

// 2026-06-29 — Country Configuration → Contracts & Legals (the base TEMPLATE
// that seeds each tenant their own copy; base-then-own). Tenants' own signed
// contracts stay in their Contracts section.
export interface CountryContractTemplate {
  id: string;
  /** The library version: 1 when created, one more each time a save changes it (2026-10-14). */
  version: number;
  /** Draft, In review or Approved - chosen in the edit form, shown on the list (2026-10-14). */
  libraryStatus: LibraryStatus;
  /** The law it rests on - "Legal basis" on the form (2026-10-14). Empty until filled and agreed. */
  legalBasis: string | null;
  name: string;
  contractType: string;
  /** The filing category: a key on the country's category list. */
  contentCategory: string;
  description: string | null;
  isDraft: boolean;
  bodyHtml: string;
  isoClause: string | null;
  relevantSectors: string[];
}
export interface CountryContractInput {
  name: string;
  contractType: string;
  /** Left out on create, the category follows the type (employment -> HR, and so on). */
  contentCategory?: string;
  description?: string | null;
  isDraft?: boolean;
  bodyHtml: string;
  isoClause?: string | null;
  relevantSectors?: string[];
  /** Read on update only: a new document starts as a draft. */
  libraryStatus?: LibraryStatus;
  legalBasis?: string | null;
}
export async function platformAdminListCountryContracts(
  countryCode: string,
): Promise<CountryContractTemplate[]> {
  return adminFetch(`/api/admin/country/${countryCode}/contracts`);
}
export async function platformAdminCreateCountryContract(
  countryCode: string,
  input: CountryContractInput,
): Promise<{ id: string; name: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/contracts`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function platformAdminUpdateCountryContract(
  countryCode: string,
  id: string,
  input: CountryContractInput,
): Promise<{ id: string; name: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/contracts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}
export async function platformAdminDeleteCountryContract(
  countryCode: string,
  id: string,
): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/contracts/${id}`, {
    method: 'DELETE',
  });
}

/** Country-level Toolbox Talk templates (base-then-own; seed each tenant's library). */
export interface CountryToolboxTemplate {
  id: string;
  /** The library version: 1 when created, one more each time a save changes it (2026-10-14). */
  version: number;
  /** Draft, In review or Approved - chosen in the edit form, shown on the list (2026-10-14). */
  libraryStatus: LibraryStatus;
  talkKey: string;
  title: string;
  /** A key on the country's category list (it was the free-text topic badge). */
  category: string;
  description: string;
  legalReference: string;
  sortOrder: number;
  roleCategoryTags: string[];
  relevantSectors: string[];
  contentSummary: string | null;
  keyPoints: string[];
  icon: string | null;
  /** What a fleet talk had (2026-10-14): one layout for both kinds of talk. */
  topic: string | null;
  durationLabel: string | null;
  keyMessage: string | null;
}
export interface CountryToolboxInput {
  talkKey?: string;
  title: string;
  category: string;
  description: string;
  legalReference: string;
  sortOrder?: number;
  roleCategoryTags?: string[];
  relevantSectors?: string[];
  contentSummary?: string | null;
  keyPoints?: string[];
  icon?: string | null;
  topic?: string | null;
  durationLabel?: string | null;
  keyMessage?: string | null;
  /** Read on update only: a new document starts as a draft. */
  libraryStatus?: LibraryStatus;
}
export async function platformAdminListCountryToolbox(
  countryCode: string,
): Promise<CountryToolboxTemplate[]> {
  return adminFetch(`/api/admin/country/${countryCode}/toolbox`);
}
export async function platformAdminCreateCountryToolbox(
  countryCode: string,
  input: CountryToolboxInput,
): Promise<{ id: string; talkKey: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/toolbox`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function platformAdminUpdateCountryToolbox(
  countryCode: string,
  id: string,
  input: CountryToolboxInput,
): Promise<{ id: string; talkKey: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/toolbox/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}
export async function platformAdminDeleteCountryToolbox(
  countryCode: string,
  id: string,
): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/toolbox/${id}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// How-to — the platform video library and the FAQs beside it
//
// READ LIVE by tenants, not provisioned. Unlike the toolbox talks above, these
// are NOT copied into per-tenant rows: editing one here changes what every
// tenant sees on their very next read. There is no "future seeds only"
// caveat, and no way to leave an old copy behind.
// ---------------------------------------------------------------------------

export interface CountryHowToVideo {
  id: string;
  videoKey: string;
  title: string;
  shelf: string;
  description: string | null;
  mediaAssetId: string | null;
  /** The picture tenants see before they press play. Null for none. */
  posterAssetId: string | null;
  sortOrder: number;
  relevantSectors: string[];
  /** Null until a file is attached — the catalogue entry can exist first. */
  media: {
    id: string;
    filename: string;
    mimeType: string;
    sizeBytes: string;
    durationSeconds: number | null;
  } | null;
}

export interface CountryHowToVideoInput {
  title: string;
  shelf: string;
  description?: string | null;
  mediaAssetId?: string | null;
  sortOrder?: number;
  relevantSectors?: string[];
}

export interface CountryFaq {
  id: string;
  faqKey: string;
  question: string;
  answer: string;
  shelf: string;
  sortOrder: number;
  relevantSectors: string[];
}

export interface CountryFaqInput {
  question: string;
  answer: string;
  shelf: string;
  sortOrder?: number;
  relevantSectors?: string[];
}

export interface PlatformMediaAsset {
  id: string;
  filename: string;
  mimeType: string;
  /** A string, not a number: BigInt does not survive JSON.stringify. */
  sizeBytes: string;
  durationSeconds: number | null;
  createdAt: string;
}

export async function platformAdminListCountryHowToVideos(
  countryCode: string,
): Promise<CountryHowToVideo[]> {
  return adminFetch(`/api/admin/country/${countryCode}/how-to-videos`);
}
export async function platformAdminCreateCountryHowToVideo(
  countryCode: string,
  input: CountryHowToVideoInput,
): Promise<{ id: string; videoKey: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/how-to-videos`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function platformAdminUpdateCountryHowToVideo(
  countryCode: string,
  id: string,
  input: CountryHowToVideoInput,
): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/how-to-videos/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}
export async function platformAdminDeleteCountryHowToVideo(
  countryCode: string,
  id: string,
): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/how-to-videos/${id}`, {
    method: 'DELETE',
  });
}

export async function platformAdminListCountryFaqs(countryCode: string): Promise<CountryFaq[]> {
  return adminFetch(`/api/admin/country/${countryCode}/faqs`);
}
export async function platformAdminCreateCountryFaq(
  countryCode: string,
  input: CountryFaqInput,
): Promise<{ id: string; faqKey: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/faqs`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function platformAdminUpdateCountryFaq(
  countryCode: string,
  id: string,
  input: CountryFaqInput,
): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/faqs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}
export async function platformAdminDeleteCountryFaq(
  countryCode: string,
  id: string,
): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/faqs/${id}`, {
    method: 'DELETE',
  });
}

export async function platformAdminListMedia(): Promise<{ media: PlatformMediaAsset[] }> {
  return adminFetch('/api/admin/media');
}

/**
 * Upload a video or audio file.
 *
 * NOT adminFetch, and it cannot be. adminFetch sets Content-Type to
 * application/json on every request; for multipart the BROWSER has to set that
 * header itself, because only it knows the boundary it generated. Setting it by
 * hand produces a body the server cannot parse and a 400 that looks like a
 * rejected file.
 *
 * The server sniffs the magic bytes and stores the DETECTED type, so a renamed
 * file is refused here rather than served back to every tenant with a video
 * Content-Type.
 */
export async function platformAdminUploadMedia(
  file: File,
  durationSeconds?: number | null,
): Promise<PlatformMediaAsset> {
  const token = getPlatformAdminToken();
  const form = new FormData();
  form.append('file', file);
  if (durationSeconds !== undefined && durationSeconds !== null) {
    form.append('durationSeconds', String(durationSeconds));
  }
  const headers = new Headers();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${getAdminApiBase()}/api/admin/media`, {
    method: 'POST',
    headers,
    body: form,
  });
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* non-json */
  }
  const wrapped = body as {
    success?: boolean;
    data?: PlatformMediaAsset;
    error?: { code: string; message: string };
  };
  if (!res.ok || wrapped?.success === false) {
    throw new Error(wrapped?.error?.message ?? `Upload failed (${res.status})`);
  }
  return wrapped.data as PlatformMediaAsset;
}

export async function platformAdminDeleteMedia(id: string): Promise<{ id: string }> {
  return adminFetch(`/api/admin/media/${id}`, { method: 'DELETE' });
}

/** Country-level Training course catalogue (central listing — provider details,
 *  fed live to every tenant in the country, sector-filtered). The platform does
 *  not deliver the training; per-tenant completions stay tenant-side. */
export interface CountryCourse {
  id: string;
  /** The library version: 1 when created, one more each time a save changes it (2026-10-14). */
  version: number;
  /** Draft, In review or Approved - chosen in the edit form, shown on the list (2026-10-14). */
  libraryStatus: LibraryStatus;
  /** Applies to: England, Wales or both (2026-10-14). */
  nations: Nation[];
  courseKey: string;
  title: string;
  policyKey: string | null;
  legalBasis: string | null;
  description: string | null;
  keyPoints: string[];
  sectorFilter: string[];
  roleCategoryTags: string[];
  enforcementLevel: number;
  gracePeriodDays: number;
  icon: string;
  /** A key on the country's category list (2026-10-13). */
  category: string;
  sortOrder: number;
}
export interface CountryCourseInput {
  courseKey?: string;
  title: string;
  category: string;
  policyKey?: string | null;
  legalBasis?: string | null;
  description?: string | null;
  keyPoints?: string[];
  sectorFilter?: string[];
  roleCategoryTags?: string[];
  enforcementLevel?: number;
  gracePeriodDays?: number;
  icon?: string | null;
  sortOrder?: number;
  /** Read on update only: a new document starts as a draft. */
  libraryStatus?: LibraryStatus;
  /** Not sent keeps it; never empty. */
  nations?: Nation[];
}
export async function platformAdminListCountryCourses(
  countryCode: string,
): Promise<CountryCourse[]> {
  return adminFetch(`/api/admin/country/${countryCode}/courses`);
}
export async function platformAdminCreateCountryCourse(
  countryCode: string,
  input: CountryCourseInput,
): Promise<{ id: string; courseKey: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/courses`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function platformAdminUpdateCountryCourse(
  countryCode: string,
  id: string,
  input: CountryCourseInput,
): Promise<{ id: string; courseKey: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/courses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}
export async function platformAdminDeleteCountryCourse(
  countryCode: string,
  id: string,
): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/courses/${id}`, {
    method: 'DELETE',
  });
}

/** Country-level Fleet (transport) toolbox talks — the central base talks fed
 *  live to every tenant's Fleet -> Toolbox Talks. An edit creates a new central
 *  revision (bumped "Rev N"). */
export interface CountryFleetTalk {
  id: string;
  title: string;
  content: string;
  keyMessage: string | null;
  topic: string | null;
  /** The talk's filing category: a key on the country's category list. */
  contentCategory: string;
  durationLabel: string | null;
  icon: string | null;
  relevantSectors: string[];
  revisionNumber: number;
  /** Draft, In review or Approved - chosen in the edit form, shown on the list (2026-10-14). */
  libraryStatus: LibraryStatus;
  /** Applies to: England, Wales or both (2026-10-14). */
  nations: Nation[];
  /** What a toolbox talk had (2026-10-14): one layout for both kinds of talk. */
  legalReference: string | null;
  description: string | null;
  contentSummary: string | null;
  roleCategoryTags: string[];
  sortOrder: number;
}
export interface CountryFleetTalkInput {
  title: string;
  content: string;
  keyMessage?: string | null;
  topic?: string | null;
  contentCategory?: string;
  durationLabel?: string | null;
  icon?: string | null;
  relevantSectors?: string[];
  changeSummary?: string;
  legalReference?: string | null;
  description?: string | null;
  contentSummary?: string | null;
  roleCategoryTags?: string[];
  sortOrder?: number;
  /** Read on update only: a new document starts as a draft. */
  libraryStatus?: LibraryStatus;
  /** Not sent keeps it; never empty. */
  nations?: Nation[];
}
export async function platformAdminListCountryFleetTalks(
  countryCode: string,
): Promise<CountryFleetTalk[]> {
  return adminFetch(`/api/admin/country/${countryCode}/fleet-talks`);
}
export async function platformAdminCreateCountryFleetTalk(
  countryCode: string,
  input: CountryFleetTalkInput,
): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/fleet-talks`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function platformAdminUpdateCountryFleetTalk(
  countryCode: string,
  id: string,
  input: CountryFleetTalkInput,
): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/fleet-talks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}
export async function platformAdminDeleteCountryFleetTalk(
  countryCode: string,
  id: string,
): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/fleet-talks/${id}`, {
    method: 'DELETE',
  });
}

/** Editable per-country sector taxonomy (the operating-sector list). */
export interface CountrySector {
  id: string;
  key: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
  /** Whether the public may choose this trade at /signup. Not the same as isActive. */
  offerOnSignup: boolean;
  /** The sections a tenant in this sector gets by default. Empty = no preset. */
  defaultSections: string[];
  /** The sector's job stage flow. Empty = the built-in flow. */
  jobStages: JobStageDef[];
}
export interface CountrySectorInput {
  key?: string; // create only — immutable thereafter
  label: string;
  sortOrder?: number;
  isActive?: boolean;
  offerOnSignup?: boolean;
  /** The sections a tenant in this sector gets by default. */
  defaultSections?: string[];
  /** Update only. [] returns the sector to the built-in flow. */
  jobStages?: JobStageDef[];
}
/** The country's customer satisfaction questionnaire. */
export async function platformAdminGetSatisfactionQuestionnaire(countryCode: string): Promise<SatisfactionQuestion[]> {
  return adminFetch(`/api/admin/country/${countryCode}/satisfaction-questionnaire`);
}
export async function platformAdminGetEmissionFactors(countryCode: string): Promise<EmissionFactor[]> {
  return adminFetch(`/api/admin/country/${countryCode}/emission-factors`);
}
export async function platformAdminSetEmissionFactors(
  countryCode: string,
  year: number,
  source: string,
  factors: Array<{ factorKey: string; kgCo2e: number }>,
): Promise<EmissionFactor[]> {
  return adminFetch(`/api/admin/country/${countryCode}/emission-factors/${year}`, {
    method: 'PUT',
    body: JSON.stringify({ source, factors }),
  });
}
/** The country's supply types beyond electricity, gas and water. */
export async function platformAdminGetSupplyTypes(countryCode: string): Promise<SupplyType[]> {
  return adminFetch(`/api/admin/country/${countryCode}/supply-types`);
}
export async function platformAdminAddSupplyType(
  countryCode: string,
  input: { label: string; unit: SupplyUnit; scope: 1 | 2 | 3 },
): Promise<SupplyType> {
  return adminFetch(`/api/admin/country/${countryCode}/supply-types`, { method: 'POST', body: JSON.stringify(input) });
}
export async function platformAdminUpdateSupplyType(
  countryCode: string,
  key: string,
  input: { label: string; scope: 1 | 2 | 3 },
): Promise<SupplyType> {
  return adminFetch(`/api/admin/country/${countryCode}/supply-types/${encodeURIComponent(key)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}
export async function platformAdminSetSatisfactionQuestionnaire(
  countryCode: string,
  questions: SatisfactionQuestion[],
): Promise<SatisfactionQuestion[]> {
  return adminFetch(`/api/admin/country/${countryCode}/satisfaction-questionnaire`, {
    method: 'PUT',
    body: JSON.stringify({ questions }),
  });
}
export async function platformAdminListCountrySectors(
  countryCode: string,
): Promise<CountrySector[]> {
  return adminFetch(`/api/admin/country/${countryCode}/sectors`);
}
export async function platformAdminCreateCountrySector(
  countryCode: string,
  input: CountrySectorInput,
): Promise<{ id: string; key: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/sectors`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function platformAdminUpdateCountrySector(
  countryCode: string,
  id: string,
  input: CountrySectorInput,
): Promise<{ id: string; key: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/sectors/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}
export async function platformAdminDeleteCountrySector(
  countryCode: string,
  id: string,
): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/sectors/${id}`, {
    method: 'DELETE',
  });
}

/** Country-content AI assist (Shane) — draft a section + check legislation.
 *  Suggestion-only; the author reviews before inserting. */
export type AiContentType = 'policy' | 'contract' | 'guidance';
export interface AiDraftResult {
  html: string;
  summary: string;
}
export interface AiLegislationFinding {
  area: string;
  concern: string;
  suggestion: string;
}
export interface AiLegislationResult {
  upToDate: boolean;
  summary: string;
  findings: AiLegislationFinding[];
}
export async function platformAdminAiDraftContent(input: {
  contentType: AiContentType;
  title: string;
  instruction: string;
  currentHtml?: string;
}): Promise<AiDraftResult> {
  return adminFetch('/api/admin/content-ai/draft', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function platformAdminAiCheckLegislation(input: {
  contentType: AiContentType;
  title: string;
  html: string;
}): Promise<AiLegislationResult> {
  return adminFetch('/api/admin/content-ai/legislation', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
/** Build the guidance OUTLINE (stage 1 — card/summary fields, no body). The
 *  main guidance body is a separate stage via platformAdminAiDraftContent. */
export interface AiGuidanceOutline {
  subtitle: string;
  whyItMatters: string;
  /** spec-45 — ten to twelve; the schema rejects a shorter list. */
  keyPoints: string[];
  /** spec-45 — four to six. */
  warnings: string[];
  platformHandles: string;
  /** spec-45 — the statutes and regulators the area rests on. Two to six. */
  sourceTags: string[];
}
export async function platformAdminAiBuildGuidanceOutline(input: {
  title: string;
  instruction: string;
}): Promise<AiGuidanceOutline> {
  return adminFetch('/api/admin/content-ai/build-guidance-outline', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
/**
 * Build the WHOLE area — card and body together, the body as BLOCKS.
 *
 * The two-stage flow left the body on the raw-HTML path and gave the draft two
 * chances to be lost between clicks. This is one call.
 */
export async function platformAdminAiBuildGuidanceArea(input: {
  title: string;
  instruction: string;
}): Promise<{ outline: AiGuidanceOutline; blocks: GuidanceBlock[] }> {
  return adminFetch('/api/admin/content-ai/build-guidance-area', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** The body on its own, as blocks, for an area whose card is already written. */
export async function platformAdminAiBuildGuidanceBody(input: {
  title: string;
  subtitle?: string;
  whyItMatters?: string;
  keyPoints?: string[];
  warnings?: string[];
  instruction?: string;
}): Promise<{ blocks: GuidanceBlock[] }> {
  return adminFetch('/api/admin/content-ai/build-guidance-body', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Revise the body to address legislation findings (apply the fixes). */
export async function platformAdminAiReviseFromLegislation(input: {
  contentType: AiContentType;
  title: string;
  html: string;
  findings: AiLegislationFinding[];
}): Promise<AiDraftResult> {
  return adminFetch('/api/admin/content-ai/revise', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** JN-ADMIN-HIDDEN-001 — per-tenant hidden sidebar sections. */
export interface SidebarSectionRef {
  key: string;
  label: string;
  group: string;
  /** Tiers this section can be set to. Absent = all three. */
  availableTiers?: SectionTierKey[];
}
export type SectionTierKey = 'base' | 'plus' | 'customised';
export interface TenantHiddenSectionsResult {
  tenantId: string;
  tenantName: string;
  hiddenSections: string[];
  /** 2026-06-12 (Shane) — per-section enabled tiers; key absent =
   *  the default, Base only. */
  sectionTiers: Record<string, SectionTierKey[]>;
  /** The tenant's operating sectors, whose presets seed its sections. */
  operatingSectors: string[];
  /** What this tenant HAS. Empty = not yet migrated, not "no sections". */
  selectedSections: string[];
  /**
   * What this tenant is ACCREDITED TO — ACCREDITATION_STANDARDS keys. Empty
   * means nothing ticked, and that is the answer: no Accreditations entries.
   */
  activeStandards: string[];
  /** Each of the tenant's sectors -> the sections its preset grants. */
  sectorPresets: Record<string, string[]>;
  catalog: SidebarSectionRef[];
}

export async function platformAdminListTenantHiddenSections(
  tenantId: string,
): Promise<TenantHiddenSectionsResult> {
  return adminFetch<TenantHiddenSectionsResult>(
    `/api/admin/tenants/${tenantId}/hidden-sections`,
  );
}

/**
 * Push the tenant's sector preset through to their sections. Additive: adds
 * what the preset grants (on Base), drops keys that are not real sections,
 * keeps agreed extras and their tiers.
 */
export async function platformAdminApplyTenantSectorPreset(tenantId: string): Promise<{
  tenantId: string;
  selectedSections: string[];
  added: string[];
  droppedDeadKeys: string[];
  keptExtras: string[];
}> {
  return adminFetch(`/api/admin/tenants/${tenantId}/apply-sector-preset`, {
    method: 'POST',
  });
}


export async function platformAdminSetTenantSelectedSections(input: {
  tenantId: string;
  selectedSections: string[];
}): Promise<{ tenantId: string; selectedSections: string[] }> {
  return adminFetch(`/api/admin/tenants/${input.tenantId}/selected-sections`, {
    method: 'POST',
    body: JSON.stringify({ selectedSections: input.selectedSections }),
  });
}


/** TIERS-002 (2026-06-12, Shane) — named section-setup presets: a
 *  whole Sidebar-sections configuration (ON/OFF + tiers) saved under
 *  a name and applied to a tenant in one go. */
export interface SectionSetupEntry {
  hidden: boolean;
  tiers: SectionTierKey[];
}
export type SectionSetupConfig = Record<string, SectionSetupEntry>;
export interface SectionSetupPreset {
  id: string;
  name: string;
  config: SectionSetupConfig;
  updatedAt: string;
}
export async function platformAdminListSectionSetupPresets(): Promise<
  SectionSetupPreset[]
> {
  return adminFetch<SectionSetupPreset[]>('/api/admin/section-setup-presets');
}
export async function platformAdminSaveSectionSetupPreset(input: {
  name: string;
  config: SectionSetupConfig;
}): Promise<{ id: string; name: string; created: boolean }> {
  return adminFetch('/api/admin/section-setup-presets', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function platformAdminDeleteSectionSetupPreset(
  presetId: string,
): Promise<{ id: string; name: string }> {
  return adminFetch(`/api/admin/section-setup-presets/${presetId}`, {
    method: 'DELETE',
  });
}
export async function platformAdminApplySectionSetupPreset(input: {
  tenantId: string;
  presetId: string;
}): Promise<{
  tenantId: string;
  presetName: string;
  hiddenSections: string[];
  sectionTiers: Record<string, SectionTierKey[]>;
}> {
  return adminFetch(`/api/admin/tenants/${input.tenantId}/apply-section-setup`, {
    method: 'POST',
    body: JSON.stringify({ presetId: input.presetId }),
  });
}

/** JN-ADMIN-NOTES-001 — internal ops notes per tenant. */
export interface TenantAdminNotes {
  tenantId: string;
  tenantName: string;
  notes: string;
  updatedAt: string | null;
}
export async function platformAdminGetTenantNotes(
  tenantId: string,
): Promise<TenantAdminNotes> {
  return adminFetch<TenantAdminNotes>(`/api/admin/tenants/${tenantId}/notes`);
}
export async function platformAdminSetTenantNotes(input: {
  tenantId: string;
  notes: string;
}): Promise<TenantAdminNotes> {
  return adminFetch<TenantAdminNotes>(
    `/api/admin/tenants/${input.tenantId}/notes`,
    { method: 'PUT', body: JSON.stringify({ notes: input.notes }) },
  );
}

/** JN-ADMIN-HEALTH-001 — per-tenant health snapshot. */
export interface TenantHealthRow {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  subscriptionTier: string;
  subscriptionStatus: string;
  createdAt: string;
  accountAgeDays: number;
  activityState: 'healthy' | 'idle' | 'dormant';
  lastUserLogin: string | null;
  daysSinceLastLogin: number | null;
  userCount: number;
  activeUsersLast7d: number;
  activeUsersLast30d: number;
  mfaEnrolledUsers: number;
  employeesActive: number;
  employeesTotal: number;
  sitesActive: number;
  failedLoginsLast24h: number;
}
export async function platformAdminGetTenantHealth(
  tenantId: string,
): Promise<TenantHealthRow> {
  return adminFetch<TenantHealthRow>(`/api/admin/tenants/${tenantId}/health`);
}

/** JN-ADMIN-MAILCHECK-001 — server email-provider health status. */
export interface EmailProviderStatus {
  provider: string;
  providerConfigured: boolean;
  apiKeyPresent: boolean;
  fromAddress: string;
  replyToAddress: string;
  publicAppOrigin: string;
  willActuallySend: boolean;
  warnings: string[];
}
export async function platformAdminGetEmailStatus(): Promise<EmailProviderStatus> {
  return adminFetch<EmailProviderStatus>('/api/admin/email-status');
}

/** PLATFORM HEALTH — partition cover (Shane, 2026-08-02).
 *
 *  Six tables are partitioned by month, and Postgres refuses a row whose month
 *  has no partition. On 1 August 2026 every insert into all six began failing
 *  on every tenant because a cron missed one night in July. It was invisible
 *  until a user hit it, which is why this is on the admin home. */
export interface PartitionHealth {
  ok: boolean;
  /** Whole months of cover on the WORST table, counting the current one. */
  minMonthsAhead: number;
  tables: Array<{ table: string; monthsAhead: number; latest: string | null }>;
  lastRunAt: string | null;
  lastRunOk: boolean | null;
  lastRunError: string | null;
  warnBelowMonths: number;
}

export async function platformAdminGetPartitionHealth(): Promise<PartitionHealth> {
  return adminFetch<PartitionHealth>('/api/admin/health/partitions');
}

/** JN-ADMIN-BROADCAST-001 — admin broadcast to tenant directors. */
export interface BroadcastResult {
  tenantsTargeted: number;
  recipientCount: number;
  sent: number;
  failed: number;
  perTenant: {
    tenantId: string;
    tenantName: string;
    sent: number;
    failed: number;
    recipients: { email: string; ok: boolean; errorMessage?: string }[];
  }[];
}
export async function platformAdminBroadcast(input: {
  tenantIds: string[];
  subject: string;
  body: string;
}): Promise<BroadcastResult> {
  return adminFetch('/api/admin/broadcast', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** JN-ADMIN-MAILTEST-001 — send a one-off test email. */
export async function platformAdminSendTestEmail(
  to: string,
): Promise<{ sent: boolean; provider: string; errorMessage?: string }> {
  return adminFetch('/api/admin/email-test', {
    method: 'POST',
    body: JSON.stringify({ to }),
  });
}

/** JN-ADMIN-FLAGS-001 — per-tenant feature flag dashboard. */
export const TENANT_FEATURE_FLAG_KEYS = [
  'biometricEnabled',
  'shiftManagementEnabled',
  'payrollEnabled',
  'subContractEnabled',
  'cashflowEnabled',
  'troncEnabled',
  'auditPackEnabled',
  'isMultiSite',
  'agentsEnabled',
  // JN-AI-FLAG-001 (2026-05-28) — keep in sync with backend
  // TENANT_FEATURE_FLAG_KEYS in platformAdminService.ts.
  'aiAssistanceEnabled',
] as const;
export type TenantFeatureFlagKey = (typeof TENANT_FEATURE_FLAG_KEYS)[number];

export interface TenantFeatureFlagsResult {
  tenantId: string;
  tenantName: string;
  flags: Record<TenantFeatureFlagKey, boolean>;
}
export async function platformAdminListTenantFeatures(
  tenantId: string,
): Promise<TenantFeatureFlagsResult> {
  return adminFetch<TenantFeatureFlagsResult>(
    `/api/admin/tenants/${tenantId}/features`,
  );
}
export async function platformAdminSetTenantFeature(input: {
  tenantId: string;
  flag: TenantFeatureFlagKey;
  value: boolean;
}): Promise<{ tenantId: string; flag: TenantFeatureFlagKey; value: boolean }> {
  return adminFetch(`/api/admin/tenants/${input.tenantId}/features`, {
    method: 'POST',
    body: JSON.stringify({ flag: input.flag, value: input.value }),
  });
}

/**
 * Clinical modules (Kris, 2026-10-01; CS-02 HM-01) — hidden per module until a
 * platform admin switches one on for a tenant, after the CSO has signed it into
 * the safety case. Switching ON needs the CSO sign-off reference; every change
 * is audited server-side.
 */
export interface TenantClinicalModule {
  key: string;
  label: string;
  enabled: boolean;
}
export async function platformAdminListTenantClinicalModules(
  tenantId: string,
): Promise<TenantClinicalModule[]> {
  return adminFetch<TenantClinicalModule[]>(`/api/admin/tenants/${tenantId}/clinical-modules`);
}
export async function platformAdminSetTenantClinicalModule(input: {
  tenantId: string;
  module: string;
  enabled: boolean;
  csoSignOffRef: string | null;
}): Promise<TenantClinicalModule[]> {
  return adminFetch<TenantClinicalModule[]>(`/api/admin/tenants/${input.tenantId}/clinical-modules`, {
    method: 'POST',
    body: JSON.stringify({ module: input.module, enabled: input.enabled, csoSignOffRef: input.csoSignOffRef }),
  });
}

/** A Switchboard ship package held for a company (SPEC-56). */
export interface SwitchboardPackage {
  id: string;
  source: string;
  madeAt: string;
  areas: string[];
  status: 'uploading' | 'waiting' | 'decided' | 'abandoned';
  itemCount: number;
  itemsReceived: number;
  fileCount: number;
  filesReceived: number;
  fileBytes: number;
  uploadedAt: string | null;
  receivedFiles: string[];
}
export interface SwitchboardPackageHeader {
  version: number;
  source: string;
  createdAt: string;
  /** The newest package from the receiving side the sender had decided (pull first). */
  pulled: string | null;
  areas: string[];
  itemCount: number;
  files: Array<{ sha256: string; fileName: string; bytes: number }>;
}
const shipBase = (tenantId: string): string => `/api/admin/tenants/${tenantId}/switchboard-packages`;
export async function platformAdminListSwitchboardPackages(tenantId: string): Promise<SwitchboardPackage[]> {
  return adminFetch<SwitchboardPackage[]>(shipBase(tenantId));
}
export async function platformAdminStartSwitchboardPackage(tenantId: string, header: SwitchboardPackageHeader): Promise<SwitchboardPackage> {
  return adminFetch<SwitchboardPackage>(shipBase(tenantId), { method: 'POST', body: JSON.stringify(header) });
}
export async function platformAdminSendSwitchboardItems(tenantId: string, packageId: string, items: unknown[]): Promise<SwitchboardPackage> {
  return adminFetch<SwitchboardPackage>(`${shipBase(tenantId)}/${packageId}/items`, { method: 'POST', body: JSON.stringify({ items }) });
}
export async function platformAdminSendSwitchboardPiece(
  tenantId: string,
  packageId: string,
  piece: { sha256: string; index: number; total: number; data: string },
): Promise<{ sha256: string; complete: boolean }> {
  return adminFetch<{ sha256: string; complete: boolean }>(`${shipBase(tenantId)}/${packageId}/pieces`, { method: 'POST', body: JSON.stringify(piece) });
}
export async function platformAdminFinishSwitchboardPackage(tenantId: string, packageId: string): Promise<SwitchboardPackage> {
  return adminFetch<SwitchboardPackage>(`${shipBase(tenantId)}/${packageId}/finish`, { method: 'POST' });
}

// DH-CONTENT-PROFILE-001 — per-tenant operating sectors (admin sets per tenant).
export interface TenantOperatingSectorsResult {
  tenantId: string;
  tenantName: string;
  sectors: string[];
}
export async function platformAdminGetTenantSectors(
  tenantId: string,
): Promise<TenantOperatingSectorsResult> {
  return adminFetch<TenantOperatingSectorsResult>(
    `/api/admin/tenants/${tenantId}/operating-sectors`,
  );
}
export async function platformAdminSetTenantSectors(input: {
  tenantId: string;
  sectors: string[];
}): Promise<TenantOperatingSectorsResult> {
  return adminFetch(`/api/admin/tenants/${input.tenantId}/operating-sectors`, {
    method: 'PUT',
    body: JSON.stringify({ sectors: input.sectors }),
  });
}

/** JN-ADMIN-FAILED-001 — failed-login report + clear lockout. */
export interface FailedLoginRow {
  userId: string | null;
  email: string;
  userName: string | null;
  tenantId: string | null;
  tenantName: string | null;
  failedCount24h: number;
  failedCountWindow: number;
  lastFailedAt: string;
  distinctIpCount: number;
  currentlyLockedOut: boolean;
}
export async function platformAdminListFailedLogins(
  days?: number,
): Promise<{ rows: FailedLoginRow[]; windowDays: number }> {
  const qs = days ? `?days=${days}` : '';
  return adminFetch(`/api/admin/failed-logins${qs}`);
}
export async function platformAdminClearLockout(
  email: string,
): Promise<{ email: string; wasLocked: boolean }> {
  return adminFetch('/api/admin/lockouts/clear', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/**
 * The Error Log (2026-10-02) — faults on every tenant. See
 * apps/api/src/services/platformAdmin/errorLogService.ts for what counts.
 */
export type ErrorLogState = 'new' | 'accepted' | 'cleared';
export type ErrorLogStateFilter = 'open' | ErrorLogState | 'all';

export interface ErrorLogRow {
  id: string;
  source: string;
  kind: string;
  state: ErrorLogState;
  occurrences: number;
  firstOccurredAt: string;
  lastOccurredAt: string;
  /** `timeZone` is the tenant's own clock (TODO 535); every date on the row reads in it. */
  tenant: { id: string; name: string; timeZone: string | null } | null;
  actor: string | null;
  user: { id: string; name: string; email: string; role: string | null } | null;
  platformAdmin: { id: string; email: string } | null;
  screen: string | null;
  action: string | null;
  method: string | null;
  path: string | null;
  status: number | null;
  code: string | null;
  message: string | null;
  errorName: string | null;
  errorMessage: string | null;
  stack: string | null;
  location: string | null;
  job: string | null;
  requestId: string | null;
  environment: string;
  userAgent: string | null;
  /** Held on the device and sent after someone signed in: the user may not be who hit it. */
  beforeSignIn: boolean;
  accepted: { at: string; by: string | null } | null;
  cleared: { at: string; by: string | null } | null;
  elsewhere: Array<{ tenantId: string; tenantName: string; occurrences: number }>;
}

export interface ErrorLogResult {
  rows: ErrorLogRow[];
  summary: { newCount: number; acceptedCount: number; tenantsWithOpen: number; clearedLast7Days: number };
  tenants: Array<{ id: string; name: string }>;
  environments: string[];
  truncated: boolean;
}

export interface ErrorLogFilters {
  state: ErrorLogStateFilter;
  days: number;
  tenantId?: string;
  kind?: string;
  environment?: string;
  search?: string;
}

export async function platformAdminListErrorLog(f: ErrorLogFilters): Promise<ErrorLogResult> {
  const qs = new URLSearchParams({ state: f.state, days: String(f.days) });
  if (f.tenantId) qs.set('tenantId', f.tenantId);
  if (f.kind) qs.set('kind', f.kind);
  if (f.environment) qs.set('environment', f.environment);
  if (f.search?.trim()) qs.set('search', f.search.trim());
  return adminFetch(`/api/admin/error-log?${qs.toString()}`);
}
export async function platformAdminAcceptErrorLog(id: string): Promise<{ id: string; state: ErrorLogState }> {
  return adminFetch(`/api/admin/error-log/${id}/accept`, { method: 'POST' });
}
export async function platformAdminClearErrorLog(id: string): Promise<{ id: string; state: ErrorLogState }> {
  return adminFetch(`/api/admin/error-log/${id}/clear`, { method: 'POST' });
}

/** The Assist bell's second count: new faults, and the latest few to accept from the bell. */
export interface ErrorLogBell {
  newCount: number;
  tenantsWithNew: number;
  latest: Array<{
    id: string;
    kind: string;
    action: string | null;
    job: string | null;
    method: string | null;
    path: string | null;
    code: string | null;
    occurrences: number;
    lastOccurredAt: string;
    tenantName: string | null;
    /** The tenant's own clock (TODO 535); null when the fault has no tenant. */
    tenantTimeZone: string | null;
  }>;
}
export async function platformAdminErrorLogBell(): Promise<ErrorLogBell> {
  return adminFetch('/api/admin/error-log/bell');
}

/** JN-ADMIN-AUDIT-001 — platform audit log feed. */
export interface PlatformAuditLogRow {
  id: string;
  createdAt: string;
  action: string;
  /** JN-ADMIN-AUDIT-DRILLIN-001 — UUID of the admin who took the
   *  action (null only if the admin was later hard-deleted). */
  platformAdminId: string | null;
  platformAdminEmail: string | null;
  platformAdminName: string | null;
  targetTenantId: string | null;
  targetTenantName: string | null;
  targetUserId: string | null;
  targetUserEmail: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  actionDetails: unknown;
}
export interface AuditLogQueryClient {
  limit?: number;
  offset?: number;
  action?: string;
  targetTenantId?: string;
  platformAdminId?: string;
  /** ISO timestamp lower bound (inclusive). */
  fromDate?: string;
  /** ISO timestamp upper bound (exclusive). */
  toDate?: string;
}
function buildAuditQs(query?: AuditLogQueryClient): string {
  const qs = new URLSearchParams();
  if (query?.limit) qs.set('limit', String(query.limit));
  if (query?.offset) qs.set('offset', String(query.offset));
  if (query?.action) qs.set('action', query.action);
  if (query?.targetTenantId) qs.set('targetTenantId', query.targetTenantId);
  if (query?.platformAdminId) qs.set('platformAdminId', query.platformAdminId);
  if (query?.fromDate) qs.set('fromDate', query.fromDate);
  if (query?.toDate) qs.set('toDate', query.toDate);
  return qs.toString() ? `?${qs.toString()}` : '';
}
export async function platformAdminListAuditLog(
  query?: AuditLogQueryClient,
): Promise<{ rows: PlatformAuditLogRow[]; total: number }> {
  return adminFetch<{ rows: PlatformAuditLogRow[]; total: number }>(
    `/api/admin/audit-log${buildAuditQs(query)}`,
  );
}

/**
 * JN-ADMIN-EXPORT-001 — fetch the audit log as a CSV blob and
 * trigger a browser download. We can't use a plain anchor / window.open
 * because the API requires the Bearer token in the Authorization header
 * and the browser won't attach it on a navigation.
 *
 * Returns metadata (row count + truncation flag) so the caller can warn
 * the user when the export hit the server-side cap.
 */
export async function platformAdminDownloadAuditLogCsv(
  query?: Omit<AuditLogQueryClient, 'limit' | 'offset'>,
): Promise<{ rowCount: number; truncated: boolean }> {
  const token = getPlatformAdminToken();
  const headers: HeadersInit = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${getAdminApiBase()}/api/admin/audit-log.csv${buildAuditQs(query)}`, {
    method: 'GET',
    headers,
  });
  if (!res.ok) {
    // Try to read a JSON error from the body (route falls back to
    // the standard error envelope on validation failure).
    let message = `Export failed (${res.status})`;
    try {
      const body = (await res.json()) as {
        error?: { message?: string };
      };
      if (body?.error?.message) message = body.error.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  const blob = await res.blob();
  const rowCount = Number(res.headers.get('X-Audit-Rows') ?? '0') || 0;
  const truncated = (res.headers.get('X-Audit-Truncated') ?? 'false') === 'true';

  // Trigger the download. Filename comes from the Content-Disposition
  // header set by the server; if the browser strips it, fall back to a
  // deterministic local name.
  const cd = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(cd);
  const filename = match
    ? match[1]
    : `jeanieiq-audit-log-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename ?? 'audit-log.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  return { rowCount, truncated };
}

/* ──────────────────────────────────────────────────────────────────
 * JN-PLATFORM-SETTINGS-001 (2026-05-29) — operator-tweakable
 * platform-level settings. Backed by the `platform_settings`
 * singleton table; surfaced in Platform Admin → Settings page.
 * ────────────────────────────────────────────────────────────────── */

export interface PlatformSettings {
  // Sessions
  jwtAccessTtl: string;
  jwtRefreshTtl: string;
  platformAdminJwtTtl: string;
  jwtImpersonationTtl: string;
  /** The window between password and second factor. */
  jwtTempTtl: string;
  /** The tenant that runs the platform and bills the rest. */
  operatorTenantId: string | null;
  // Email
  emailProvider: string;
  emailFrom: string;
  emailReplyTo: string;
  publicAppOrigin: string;
  /** Comma-separated staff addresses notified on an early-access sign-up.
   *  Edited on the Early Access screen, not this one — see EarlyAccessPage. */
  earlyAccessNotifyTo: string;
  // Trials
  trialPeriodDays: number;
  /** How long a locum's DBS certificate COPY is kept; the details stay for good. */
  locumDbsRetentionDays: number;
  locumClosedAccountDays: number;
  // New-tenant defaults
  newTenantBiometric: boolean;
  newTenantShiftMgmt: boolean;
  newTenantAssistLink: boolean;
  newTenantAiAssistance: boolean;
  /**
   * "OVERRIDE ALL SHIFT patterns on" (2026-08-30) - a testing switch. Reveals
   * the shift entries that a tenant's own configuration hides: the per-tenant
   * shiftManagementEnabled flag, and the pharmacy sector rule that hides the
   * generic Shift Builder. Platform-wide, off by default.
   *
   * It sits beside the new-tenant defaults on screen but is NOT one of them - a
   * creation-time default would do nothing for the tenants that already exist,
   * which are the ones being tested.
   *
   * It SHOWS, it does not GRANT: routes keep their own gates.
   */
  overrideAllShiftPatterns: boolean;
  /** TODO 530c — require two-step sign-in for Platform Admin. */
  platformAdminMfaRequired: boolean;
  /** BIO-001 (2026-06-12) — platform-wide biometric MASTER switch.
   *  False = biometric auth dark across every tenant regardless of
   *  their own setting. GDPR three-layer model: master AND tenant
   *  opt-in AND per-employee enrolment consent. */
  biometricMasterEnabled: boolean;
  // Scope 3 — Jeanie monthly spend cap, platform-wide. 0 = use the
  // hardcoded fallback in spendCap.ts (currently £100/month).
  jeanieMonthlyCapPence: number;
  /** The public sign-up's terms and privacy notice (spec-54); Sign up stays hidden until all four are set. */
  signupEnabled: boolean;
  signupTermsUrl: string;
  signupTermsVersion: string;
  signupPrivacyUrl: string;
  signupPrivacyVersion: string;
  /** Days a director has to sign newly published terms before being held. */
  signupDaysToSign: number;
  /** When the terms version setting last changed (read-only). */
  signupTermsVersionSetAt: string | null;
}

/** Scope 3 — masked API key preview. Never carries plaintext. */
export interface ApiKeyPreview {
  set: boolean;
  last4: string | null;
  envOverride: boolean;
}

export type ApiKeyName =
  | 'anthropic'
  | 'openai'
  | 'resend'
  | 'awsAccessKeyId'
  | 'awsSecretAccessKey'
  | 'companiesHouse';

export interface ApiKeyPreviews {
  anthropic: ApiKeyPreview;
  openai: ApiKeyPreview;
  resend: ApiKeyPreview;
  awsAccessKeyId: ApiKeyPreview;
  awsSecretAccessKey: ApiKeyPreview;
  companiesHouse: ApiKeyPreview;
}

export interface PlatformSettingsResult {
  settings: PlatformSettings;
  /** Keys whose effective value comes from an env var — DB / UI changes
   *  for these keys are ignored at runtime. Surfaced for warning UI. */
  envOverrides: (keyof PlatformSettings)[];
  /** Scope 3 — masked API key previews (never plaintext). */
  apiKeys: ApiKeyPreviews;
}

export type PlatformSettingsPatch = Partial<PlatformSettings> & {
  /** The outgoing operator's slug. A barrier the server asks for, not a setting. */
  confirmOperatorChange?: string;
};

export async function platformAdminGetSettings(): Promise<PlatformSettingsResult> {
  return adminFetch<PlatformSettingsResult>('/api/admin/settings');
}

export async function platformAdminUpdateSettings(
  patch: PlatformSettingsPatch,
): Promise<PlatformSettingsResult> {
  return adminFetch<PlatformSettingsResult>('/api/admin/settings', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/** Scope 3 — rotate (or clear by passing empty string) a single API
 *  key. Plaintext goes only over the auth'd HTTPS connection; server
 *  encrypts before write and the response never returns plaintext. */
export async function platformAdminRotateApiKey(
  keyName: ApiKeyName,
  value: string,
): Promise<PlatformSettingsResult> {
  return adminFetch<PlatformSettingsResult>('/api/admin/settings/api-key', {
    method: 'PATCH',
    body: JSON.stringify({ keyName, value }),
  });
}

// ---------------------------------------------------------------------------
// JN-EARLY-ACCESS-001 (2026-09-19) — the Early Access screen.
// ---------------------------------------------------------------------------

export interface EarlyAccessSubmission {
  id: string;
  name: string;
  email: string;
  sector: string;
  employeeBand: string;
  turnoverBand: string;
  source: string;
  consentedAt: string;
  createdAt: string;
  confirmedAt: string | null;
  unsubscribedAt: string | null;
  convertedAt: string | null;
  convertedTenantId: string | null;
  onboardingDate: string | null;
}

// ---------------------------------------------------------------------------
// Sign-up requests (spec-54 area E)
// ---------------------------------------------------------------------------

export type SignupRequestStatus = 'unconfirmed' | 'mismatch' | 'awaiting_review' | 'refused' | 'done';

export interface SignupRequestRow {
  id: string;
  status: SignupRequestStatus;
  createdAt: string;
  confirmedAt: string | null;
  companyName: string;
  companyNumber: string;
  postcode: string;
  directorName: string;
  directorEmail: string;
  details: {
    sector: string; registeredAddressLine1: string; registeredAddressLine2?: string | null; registeredAddressCity: string;
    registeredAddressPostcode: string; vatNumber?: string | null; companyTelephone?: string | null; accountsEmail?: string | null;
    directorPhone?: string | null; headcount: number; addonKeys: string[]; offerCode?: string | null;
  };
  stops: Array<{ code: string; words: string }>;
  companiesHouse: { companyNumber: string; companyName: string; status: string; postcode: string | null; address: string | null } | null;
  marketingConsent: boolean;
  unsubscribed: boolean;
  termsVersion: string;
  privacyVersion: string;
  notes: Array<{ at: string; by: string; note: string }>;
  decidedBy: string | null;
  decidedAt: string | null;
  refusedReason: string | null;
  tenantId: string | null;
}

export async function platformAdminListSignupRequests(status?: SignupRequestStatus): Promise<{ requests: SignupRequestRow[] }> {
  return adminFetch(`/api/admin/signup-requests${status ? `?status=${status}` : ''}`);
}
export async function platformAdminApproveSignupRequest(id: string, dealPounds?: number): Promise<SignupRequestRow> {
  return adminFetch(`/api/admin/signup-requests/${encodeURIComponent(id)}/approve`, {
    method: 'POST', body: JSON.stringify(dealPounds !== undefined ? { dealPounds } : {}),
  });
}
export async function platformAdminRefuseSignupRequest(id: string, reason: string): Promise<SignupRequestRow> {
  return adminFetch(`/api/admin/signup-requests/${encodeURIComponent(id)}/refuse`, { method: 'POST', body: JSON.stringify({ reason }) });
}
export async function platformAdminNoteSignupRequest(id: string, note: string): Promise<SignupRequestRow> {
  return adminFetch(`/api/admin/signup-requests/${encodeURIComponent(id)}/note`, { method: 'POST', body: JSON.stringify({ note }) });
}

export async function platformAdminListEarlyAccess(): Promise<{
  submissions: EarlyAccessSubmission[];
}> {
  return adminFetch('/api/admin/early-access');
}

export async function platformAdminSendEarlyAccessOnboardingEmail(
  submissionId: string,
  onboardingDate: string,
): Promise<void> {
  await adminFetch(`/api/admin/early-access/${submissionId}/send-onboarding-email`, {
    method: 'POST',
    body: JSON.stringify({ onboardingDate }),
  });
}

// ---------------------------------------------------------------------------
// spec-45 Phase D — the glossary pass.
//
// Two calls on purpose. The first proposes and resolves and writes nothing, so
// the reviewer sees exactly what would be created — including which terms are
// already defined and would be skipped. The second saves what survived review.
// ---------------------------------------------------------------------------

export interface GlossaryProposalRow {
  term: string;
  abbreviation: string | null;
  definition: string;
  preview: string;
  legalSource: string | null;
  /** Code-set from the area — not proposed by the model. */
  category: string;
  guidanceArea: string;
  relevantSectors: string[];
  relevantSections: string[];
  /** Non-null when the term already exists; it is linked, not duplicated. */
  existingId: string | null;
}

export async function platformAdminProposeGlossaryTerms(
  countryCode: string,
  areaSlug: string,
  input: {
    title: string;
    subtitle?: string;
    whyItMatters?: string;
    keyPoints?: string[];
  },
): Promise<{ terms: GlossaryProposalRow[]; categories: string[]; alreadyLinked: string[] }> {
  return adminFetch(
    `/api/admin/country/${countryCode}/guidance/${encodeURIComponent(areaSlug)}/glossary-proposals`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export async function platformAdminSaveGlossaryProposals(
  countryCode: string,
  areaSlug: string,
  terms: GlossaryProposalRow[],
): Promise<{ created: number; skipped: number; ids: string[] }> {
  return adminFetch(
    `/api/admin/country/${countryCode}/guidance/${encodeURIComponent(areaSlug)}/glossary-proposals/save`,
    { method: 'POST', body: JSON.stringify({ terms }) },
  );
}

// ---------------------------------------------------------------------------
// JeanieIQ Directory — country advertising listings (2026-08-17)
//
// Authored once per country and read by every tenant in it. Archive and
// reinstate live here rather than on the tenant, because the listings are
// shared: Shane, "no archive on the tennent side... its in ADMIN. that is our
// control and also give it a reinstate please if they come back".
// ---------------------------------------------------------------------------

export interface CountryDirectoryListing {
  id: string;
  countryCode: string;
  ref: string;
  logo: string;
  companyName: string;
  category: string;
  subCategories: string[];
  regionsDisplay: string | null;
  description: string;
  regions: string[];
  specialisms: string[];
  websiteUrl: string | null;
  email: string | null;
  phone: string | null;
  tier: 'premium' | 'featured' | 'standard';
  reviewCount: number;
  averageRating: number;
  isVerified: boolean;
  verifiedAccreditations: string[];
  pricingGuide: string | null;
  offerForBaseUsers: string | null;
  sortOrder: number;
  archived: boolean;
}

export interface CountryDirectoryInput {
  companyName: string;
  category: string;
  logo?: string;
  subCategories?: string[];
  regionsDisplay?: string | null;
  description?: string;
  regions?: string[];
  specialisms?: string[];
  websiteUrl?: string | null;
  email?: string | null;
  phone?: string | null;
  tier?: 'premium' | 'featured' | 'standard';
  isVerified?: boolean;
  pricingGuide?: string | null;
  offerForBaseUsers?: string | null;
}

export async function platformAdminListCountryDirectory(
  countryCode: string,
  opts: { archived?: boolean } = {},
): Promise<CountryDirectoryListing[]> {
  const q = opts.archived ? '?archived=true' : '';
  return adminFetch(`/api/admin/country/${countryCode}/directory${q}`);
}

export async function platformAdminCreateCountryDirectory(
  countryCode: string,
  input: CountryDirectoryInput,
): Promise<CountryDirectoryListing> {
  return adminFetch(`/api/admin/country/${countryCode}/directory`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function platformAdminUpdateCountryDirectory(
  countryCode: string,
  id: string,
  input: CountryDirectoryInput,
): Promise<CountryDirectoryListing> {
  return adminFetch(`/api/admin/country/${countryCode}/directory/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function platformAdminArchiveCountryDirectory(
  countryCode: string,
  id: string,
): Promise<CountryDirectoryListing> {
  return adminFetch(`/api/admin/country/${countryCode}/directory/${id}/archive`, {
    method: 'POST',
  });
}

export async function platformAdminReinstateCountryDirectory(
  countryCode: string,
  id: string,
): Promise<CountryDirectoryListing> {
  return adminFetch(`/api/admin/country/${countryCode}/directory/${id}/reinstate`, {
    method: 'POST',
  });
}

/**
 * The directory's enquiry wording + subject list for a country.
 *
 * Shane, 2026-08-17: the "How this works" notice states the commercial model —
 * "we cannot have that if we charge a fee ti advertise for a period" — and the
 * subject list "cannot be hard coded". Both are editable country content.
 */
export interface CountryDirectorySettings {
  countryCode: string;
  enquiryNotice: string;
  subjectAreas: string[];
}

export async function platformAdminGetCountryDirectorySettings(
  countryCode: string,
): Promise<CountryDirectorySettings> {
  return adminFetch(`/api/admin/country/${countryCode}/directory/settings`);
}

export async function platformAdminSaveCountryDirectorySettings(
  countryCode: string,
  input: { enquiryNotice: string; subjectAreas: string[] },
): Promise<CountryDirectorySettings> {
  return adminFetch(`/api/admin/country/${countryCode}/directory/settings`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/* ─────────────────────────── RAMS templates (country) ───────────────────────────
 *
 * The central RAMS library, authored in Country Configuration and fed to every
 * tenant in the country whose sectors it applies to (2026-08-31).
 *
 * `bodyJson` is the document itself — { shortDescription, sections: [{ key,
 * title, body }] } — and is passed through untouched. The job screen authors and
 * edits it; this client only carries it.
 */
export interface CountryRamsTemplate {
  id: string;
  /** The library version: 1 when created, one more each time a save changes it (2026-10-14). */
  version: number;
  /** Draft, In review or Approved - chosen in the edit form, shown on the list (2026-10-14). */
  libraryStatus: LibraryStatus;
  /** The law it rests on - "Legal basis" on the form (2026-10-14). Empty until filled and agreed. */
  legalBasis: string | null;
  templateKey: string;
  name: string;
  /** The kind of work it covers ("Work type"). */
  category: string;
  /** The filing category: a key on the country's category list. */
  contentCategory: string;
  icon: string;
  shortDescription: string;
  relevantSectors: string[];
  sortOrder: number;
  bodyJson: unknown;
}

export interface CountryRamsInput {
  templateKey?: string;
  name: string;
  category?: string;
  contentCategory?: string;
  icon?: string;
  shortDescription?: string;
  relevantSectors?: string[];
  sortOrder?: number;
  /** Omit to keep the stored document. See the service note on why. */
  bodyJson?: unknown;
  /** Read on update only: a new document starts as a draft. */
  libraryStatus?: LibraryStatus;
  legalBasis?: string | null;
}

export async function platformAdminListCountryRams(
  countryCode: string,
): Promise<CountryRamsTemplate[]> {
  return adminFetch(`/api/admin/country/${countryCode}/rams`);
}
export async function platformAdminCreateCountryRams(
  countryCode: string,
  input: CountryRamsInput,
): Promise<{ id: string; templateKey: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/rams`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function platformAdminUpdateCountryRams(
  countryCode: string,
  id: string,
  input: CountryRamsInput,
): Promise<{ id: string; templateKey: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/rams/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}
export async function platformAdminDeleteCountryRams(
  countryCode: string,
  id: string,
): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/rams/${id}`, { method: 'DELETE' });
}

/** The generic Health & Safety templates (2026-10-20): which form one fills. */
export type HsTemplateKind = 'risk_assessment' | 'fire' | 'coshh';

export interface CountryHsTemplate {
  id: string;
  version: number;
  libraryStatus: LibraryStatus;
  legalBasis: string | null;
  templateKey: string;
  kind: HsTemplateKind;
  name: string;
  shortDescription: string;
  /** The fields of the form it fills, for its kind. */
  bodyJson: Record<string, unknown>;
  relevantSectors: string[];
  sortOrder: number;
  updatedAt: string;
}

export interface CountryHsTemplateInput {
  templateKey?: string;
  kind: HsTemplateKind;
  name: string;
  shortDescription?: string;
  bodyJson: Record<string, unknown>;
  relevantSectors?: string[];
  sortOrder?: number;
  libraryStatus?: LibraryStatus;
  legalBasis?: string | null;
}

export async function platformAdminListCountryHsTemplates(countryCode: string): Promise<CountryHsTemplate[]> {
  return adminFetch(`/api/admin/country/${countryCode}/hs-templates`);
}
export async function platformAdminCreateCountryHsTemplate(
  countryCode: string,
  input: CountryHsTemplateInput,
): Promise<{ id: string; templateKey: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/hs-templates`, { method: 'POST', body: JSON.stringify(input) });
}
export async function platformAdminUpdateCountryHsTemplate(
  countryCode: string,
  id: string,
  input: CountryHsTemplateInput,
): Promise<{ id: string; templateKey: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/hs-templates/${id}`, { method: 'PUT', body: JSON.stringify(input) });
}
export async function platformAdminDeleteCountryHsTemplate(countryCode: string, id: string): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/hs-templates/${id}`, { method: 'DELETE' });
}

/**
 * The central memo library (2026-08-31).
 *
 * `memoNumber` is the library key the tenant's "+ Add document" picker adopts by,
 * so it is settable on create and absent from the input on update.
 */
export interface CountryMemoTemplate {
  id: string;
  memoNumber: number;
  title: string;
  description: string;
  body: string;
  category: string;
  /** The library version: 1 when created, one more each time a save changes it (2026-10-14). */
  version: number;
  /** Draft, In review or Approved - chosen in the edit form, shown on the list (2026-10-14). */
  libraryStatus: LibraryStatus;
  /** The law it rests on - "Legal basis" on the form (2026-10-14). Empty until filled and agreed. */
  legalBasis: string | null;
  scope: string;
  roleNames: string[];
  memoKind: string;
  relevantSectors: string[];
  sortOrder: number;
}

export interface CountryMemoInput {
  memoNumber?: number;
  title: string;
  description?: string;
  /** Omit to keep the stored memo. See the service note on why. */
  body?: string;
  category?: string;
  scope?: string;
  roleNames?: string[];
  memoKind?: string;
  relevantSectors?: string[];
  sortOrder?: number;
  /** Read on update only: a new document starts as a draft. */
  libraryStatus?: LibraryStatus;
  legalBasis?: string | null;
}

export async function platformAdminListCountryMemos(
  countryCode: string,
): Promise<CountryMemoTemplate[]> {
  return adminFetch(`/api/admin/country/${countryCode}/memos`);
}
export async function platformAdminCreateCountryMemo(
  countryCode: string,
  input: CountryMemoInput,
): Promise<{ id: string; memoNumber: number }> {
  return adminFetch(`/api/admin/country/${countryCode}/memos`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function platformAdminUpdateCountryMemo(
  countryCode: string,
  id: string,
  input: CountryMemoInput,
): Promise<{ id: string; memoNumber: number }> {
  return adminFetch(`/api/admin/country/${countryCode}/memos/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}
export async function platformAdminDeleteCountryMemo(
  countryCode: string,
  id: string,
): Promise<{ id: string }> {
  return adminFetch(`/api/admin/country/${countryCode}/memos/${id}`, { method: 'DELETE' });
}

/**
 * Country Configuration → Inductions (2026-10-14): the base site induction for
 * each sign-in pathway. Three per country, saved by pathway; see
 * `saveCountryInductionTemplate` on the API.
 */
export type InductionPathwayKey = 'meeting_only' | 'full_site' | 'onboarding';

export interface CountryInductionTemplate {
  id: string;
  countryCode: string;
  pathway: InductionPathwayKey;
  title: string;
  description: string;
  body: string;
  version: string;
  /** Offered to companies only when Approved. */
  libraryStatus: LibraryStatus;
  updatedAt: string;
}

export interface CountryInductionInput {
  title: string;
  description?: string;
  body: string;
  version?: string;
  /** Read on an edit only: one written for the first time starts as a draft. */
  libraryStatus?: LibraryStatus;
}

export async function platformAdminListCountryInductions(
  countryCode: string,
): Promise<CountryInductionTemplate[]> {
  return adminFetch(`/api/admin/country/${countryCode}/inductions`);
}
export async function platformAdminSaveCountryInduction(
  countryCode: string,
  pathway: InductionPathwayKey,
  input: CountryInductionInput,
): Promise<{ id: string; pathway: string; created: boolean }> {
  return adminFetch(`/api/admin/country/${countryCode}/inductions/${pathway}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/**
 * JeanieIQ Assist — asking a tenant for access.
 *
 * This grants nothing. It puts a request in front of the tenant; they accept,
 * decline, or ignore it. Nothing lets an admin in except a live grant, which the
 * API re-checks on every request.
 */
export interface AssistGrantSummary {
  id: string;
  status: string;
  reason: string;
  requestedByEmail: string;
  requestedAt: string;
  expiresAt: string | null;
}

export async function platformAdminRequestAssist(
  tenantId: string,
  reason: string,
  ticketRef?: string,
): Promise<AssistGrantSummary> {
  return adminFetch(`/api/admin/tenants/${tenantId}/assist-request`, {
    method: 'POST',
    body: JSON.stringify({ reason, ...(ticketRef ? { ticketRef } : {}) }),
  });
}

/**
 * The JeanieIQ GDPR Vault — the operator's own governance documents, an area of
 * Country Configuration. Each document is written for one country (2026-10-03),
 * so every call names it, like the other areas' do. Platform-admin only; there
 * is no tenant-facing equivalent by design.
 */
export interface PlatformDocumentSummary {
  id: string;
  countryCode: string;
  slug: string;
  title: string;
  /** The document's TYPE - gdpr, contract, policy, certificate, other. */
  category: string;
  /** The filing category: a key on the country's category list. */
  contentCategory: string;
  status: string;
  version: number;
  /** The law it rests on - "Legal basis" on the form (2026-10-14). Empty until filled and agreed. */
  legalBasis: string | null;
  updatedAt: string;
  length: number;
}

export interface PlatformDocumentDetail extends PlatformDocumentSummary {
  body: string;
  revisions: Array<{ version: number; title: string; note: string | null; createdAt: string }>;
}

const vaultPath = (countryCode: string, id?: string): string =>
  `/api/admin/country/${countryCode}/documents${id ? `/${id}` : ''}`;

/** JeanieIQ Documents or Pharmacy Documents. */
export type PlatformDocumentLibrary = 'jeanieiq' | 'pharmacy';

export async function platformAdminListDocuments(
  countryCode: string,
  library: PlatformDocumentLibrary = 'jeanieiq',
): Promise<PlatformDocumentSummary[]> {
  return adminFetch(`${vaultPath(countryCode)}?library=${library}`);
}
export async function platformAdminGetDocument(countryCode: string, id: string): Promise<PlatformDocumentDetail> {
  return adminFetch(vaultPath(countryCode, id));
}
export async function platformAdminCreateDocument(
  countryCode: string,
  input: { title: string; category?: string; contentCategory?: string; body?: string; legalBasis?: string | null; library?: PlatformDocumentLibrary },
): Promise<{ id: string; slug: string }> {
  return adminFetch(vaultPath(countryCode), { method: 'POST', body: JSON.stringify(input) });
}
export async function platformAdminUpdateDocument(
  countryCode: string,
  id: string,
  input: { title?: string; category?: string; contentCategory?: string; status?: string; body?: string; note?: string; legalBasis?: string | null },
): Promise<{ id: string; version: number }> {
  return adminFetch(vaultPath(countryCode, id), { method: 'PUT', body: JSON.stringify(input) });
}
export async function platformAdminArchiveDocument(countryCode: string, id: string): Promise<{ id: string }> {
  return adminFetch(vaultPath(countryCode, id), { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// The JeanieIQ Assist queue — tenants who have asked US (2026-09-24)
//
// Shane: *"its 2 step they ask we see and accept they agree entry"*. Accepting
// one raises an ordinary access request against that tenant, so they still
// approve entry themselves; this only decides which ask we pick up.
// ---------------------------------------------------------------------------

export interface AssistQueueEntry {
  id: string;
  tenantId: string;
  tenantName: string;
  /** The tenant's own clock (TODO 535): the request and its thread read in it. */
  tenantTimeZone: string;
  /** ASK-YYYY-NNN, the tenant's own reference for it. */
  reference: string;
  subject: string;
  body: string;
  /** low | normal | high */
  urgency: string;
  /** open | accepted */
  status: string;
  raisedByEmail: string;
  /** The person who hit the problem — we enter as them. */
  raisedByUserId: string;
  raisedAt: string;
  acceptedAt: string | null;
  /** Null until access is ASKED FOR — accepting a request raises no grant. */
  grantId: string | null;
  /** pending | active | declined | ended | expired */
  grantStatus: string | null;
  /** read_only | full — how far in the TENANT let us. Null until they answer. */
  grantAccessLevel: string | null;
  /** What it turned out to be. Null until somebody closes it. */
  resolution: string | null;
  resolvedAt: string | null;
  /** tenant | jeanieiq — which side closed it. */
  resolvedBy: 'tenant' | 'jeanieiq' | null;
}

export async function platformAdminAssistQueueCount(): Promise<{ open: number }> {
  return adminFetch('/api/admin/assist-requests/count');
}

export async function platformAdminAssistQueue(): Promise<AssistQueueEntry[]> {
  return adminFetch('/api/admin/assist-requests');
}

export async function platformAdminAcceptAssistRequest(
  id: string,
): Promise<AssistQueueEntry> {
  return adminFetch(`/api/admin/assist-requests/${id}/accept`, { method: 'POST' });
}

/**
 * The conversation on an assist request. A reply from here is from JEANIEIQ — the
 * row carries sender_admin_id, not a tenant user id, which is why this could not
 * reuse staff chat.
 */
export interface AssistMessage {
  id: string;
  body: string;
  sentAt: string;
  /** tenant | jeanieiq */
  from: 'tenant' | 'jeanieiq';
  sender: string;
}

export async function platformAdminAssistMessages(id: string): Promise<AssistMessage[]> {
  return adminFetch(`/api/admin/assist-requests/${id}/messages`);
}

export async function platformAdminPostAssistMessage(
  id: string,
  body: string,
): Promise<AssistMessage> {
  return adminFetch(`/api/admin/assist-requests/${id}/messages`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

/**
 * Rung two — ask to look at their platform for this request. Separate from
 * accepting it: Shane, 2026-09-24, *"there doesnt have to be a time limit if its
 * just messaging"*. The TENANT answers this and chooses how far in we get.
 */
export async function platformAdminRequestAssistAccess(
  id: string,
): Promise<AssistQueueEntry> {
  return adminFetch(`/api/admin/assist-requests/${id}/request-access`, { method: 'POST' });
}

/** Close a request, saying what it turned out to be. Logged on both trails. */
export async function platformAdminResolveAssistRequest(
  id: string,
  resolution: string,
): Promise<AssistQueueEntry> {
  return adminFetch(`/api/admin/assist-requests/${id}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ resolution }),
  });
}

/**
 * What we have already finished. Shane, 2026-09-24: *"when we have finished
 * assist and its cloesed. it needs to go into a completed job.. not disappear"*.
 * Newest first — a record to look something up in, not a list of work.
 */
export async function platformAdminCompletedAssistRequests(): Promise<AssistQueueEntry[]> {
  return adminFetch('/api/admin/assist-requests/completed');
}

// ---------------------------------------------------------------------------
// Rung four — the internal engineering item (2026-09-24)
//
// Shane: *"if this is not helping and its a techniocal fault that is when its
// raised to the next technical level"*. These are OURS. Nothing here is visible
// to a tenant; their request carries only a timestamp saying it went to the
// engineers, so their page does not go quiet on them.
// ---------------------------------------------------------------------------

export interface AssistEscalation {
  id: string;
  requestId: string;
  /** The assist reference the customer knows it by — there is no second number. */
  reference: string;
  tenantId: string;
  tenantName: string;
  /** The tenant's own clock (TODO 535): the escalation and its notes read in it. */
  tenantTimeZone: string;
  subject: string;
  /** The fault as WE understand it, not the customer's words. */
  summary: string;
  detail: string;
  /** low | normal | high */
  severity: string;
  /** open | in_progress | resolved */
  status: string;
  raisedByAdminId: string;
  assignedToAdminId: string | null;
  resolution: string | null;
  resolvedAt: string | null;
  raisedAt: string;
}

export interface AssistEscalationNote {
  id: string;
  body: string;
  author: string;
  sentAt: string;
}

export async function platformAdminEscalations(): Promise<AssistEscalation[]> {
  return adminFetch('/api/admin/assist-escalations');
}

export async function platformAdminEscalationCount(): Promise<{ open: number }> {
  return adminFetch('/api/admin/assist-escalations/count');
}

export async function platformAdminEscalate(
  requestId: string,
  input: { summary: string; detail: string; severity?: 'low' | 'normal' | 'high' },
): Promise<AssistEscalation> {
  return adminFetch(`/api/admin/assist-requests/${requestId}/escalate`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function platformAdminUpdateEscalation(
  id: string,
  input: { status?: 'open' | 'in_progress' | 'resolved'; assignToSelf?: boolean; resolution?: string },
): Promise<AssistEscalation> {
  return adminFetch(`/api/admin/assist-escalations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export async function platformAdminEscalationNotes(id: string): Promise<AssistEscalationNote[]> {
  return adminFetch(`/api/admin/assist-escalations/${id}/notes`);
}

export async function platformAdminAddEscalationNote(
  id: string,
  body: string,
): Promise<AssistEscalationNote> {
  return adminFetch(`/api/admin/assist-escalations/${id}/notes`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

/**
 * Subscription billing worklist — JeanieIQ collecting its own subscriptions.
 * Mirrors worklistService.ts's kinds. `cause` is already display text
 * for `ledger_stuck`, `retry_unconfirmed`, `collection_unconfirmed`,
 * `collection_cancelled` and `old_direct_debit_still_live` (shown as-is); for
 * `payment_failed` it is a raw GoCardless code (e.g. `refer_to_payer`), and
 * for `collection_refused` GoCardless's own refusal message. Only the
 * informational kinds (`retry_unconfirmed`, `collection_unconfirmed`,
 * `collection_cancelled`, `old_direct_debit_still_live`) carry no button —
 * they resolve on their own as the situation moves on. `renewal_blocked`
 * (Ruling 28) is a term the daily raise could not renew; its cause is display
 * text naming the next step, and the row links to the tenant.
 */
export interface SubscriptionWorklistRow {
  kind: 'payment_failed' | 'direct_debit_ended' | 'charged_back' | 'collection_unconfirmed' | 'collection_cancelled'
    | 'collection_refused' | 'ledger_stuck' | 'retry_unconfirmed' | 'old_direct_debit_still_live' | 'renewal_blocked'
    | 'self_signup_no_direct_debit'
    | 'closure_requested'
    | 'addon_removal_requested'
    | 'headcount_over_band'
    | 'band_move_requested';
  payerTenantId: string; payerName: string; collectionId: string | null; mandateId: string | null; invoiceId: string | null;
  invoiceRef: string | null; amountPence: number | null; cause: string | null; nextAutoRetryAt: string | null; at: string;
  /** The payer tenant's own clock (TODO 535). */
  payerTimeZone: string;
}
export async function platformAdminSubscriptionWorklist(): Promise<SubscriptionWorklistRow[]> {
  return adminFetch('/api/admin/subscription-billing/worklist');
}
export async function platformAdminRetryCollection(collectionId: string): Promise<{ status: string; chargeDate: string | null }> {
  return adminFetch(`/api/admin/subscription-billing/collections/${collectionId}/retry`, { method: 'POST', body: JSON.stringify({}) });
}
export async function platformAdminMarkLedgerReversed(collectionId: string): Promise<void> {
  await adminFetch(`/api/admin/subscription-billing/collections/${collectionId}/ledger-reversed`, { method: 'POST', body: JSON.stringify({}) });
}
/** For `ledger_stuck`: a person has checked the customer's receipts and confirmed the payment is (or now is) on the ledger. */
export async function platformAdminMarkLedgerRecorded(collectionId: string): Promise<void> {
  await adminFetch(`/api/admin/subscription-billing/collections/${collectionId}/ledger-recorded`, { method: 'POST', body: JSON.stringify({}) });
}
/** For `collection_refused`: retire the refused collection so the next daily run collects the invoice afresh. */
export async function platformAdminRecollectCollection(collectionId: string): Promise<void> {
  await adminFetch(`/api/admin/subscription-billing/collections/${collectionId}/recollect`, { method: 'POST', body: JSON.stringify({}) });
}
export async function platformAdminSendDirectDebitLink(tenantId: string): Promise<{ sentTo: string; expiresAt: string }> {
  return adminFetch(`/api/admin/tenants/${tenantId}/direct-debit-link`, { method: 'POST', body: JSON.stringify({}) });
}

/*
 * IS GOCARDLESS CONNECTED? (2026-10-16). Mirrors connectionService.ts.
 *
 * NEITHER SHAPE CARRIES A CREDENTIAL, and neither ever should: the token lives
 * in the server environment, and a masked one on a screen is still a secret
 * with some of it given away. What is here is whether it works, which account
 * it points at, and when GoCardless last said anything to us - the last being
 * the only one that proves it is working rather than merely configured.
 */
export interface SubscriptionConnection {
  /** A token is SET. Says nothing about whether it works - that is the test. */
  configured: boolean;
  environment: 'sandbox' | 'live';
  /** Without it, webhooks are rejected and collections never settle, silently. */
  hasWebhookSecret: boolean;
  /** JeanieIQ's own operator tenant: no operator, no ledger to collect into. */
  operatorTenantSet: boolean;
  lastWebhookAt: string | null;
  lastWebhookAction: string | null;
  webhooksSeen: number;
  /** True where nothing is stored, so an environment variable is the one in effect. */
  tokenFromEnv: boolean;
  webhookSecretFromEnv: boolean;
}
export interface SubscriptionConnectionTest {
  ok: true;
  environment: 'sandbox' | 'live';
  /** WHICH GoCardless account the token belongs to. A valid token for the wrong account is still wrong. */
  creditorName: string | null;
  creditorId: string | null;
}
export async function platformAdminSubscriptionConnection(): Promise<SubscriptionConnection> {
  return adminFetch('/api/admin/subscription-billing/connection');
}
/** A POST for a read: it reaches a third party, so it is not something a link or a prefetch should trigger. */
export async function platformAdminTestSubscriptionConnection(): Promise<SubscriptionConnectionTest> {
  return adminFetch('/api/admin/subscription-billing/connection/test', { method: 'POST', body: JSON.stringify({}) });
}

/**
 * Set, replace or clear one of the two GoCardless keys. Pass '' to clear.
 *
 * WRITE-ONLY, and there is deliberately no companion that reads one back.
 * The response is the refreshed connection STATUS — whether each key is set,
 * never what it is — so the panel updates from the same call that saved.
 */
export async function platformAdminSetGoCardlessKey(
  which: 'gocardless' | 'gocardlessWebhookSecret',
  value: string,
): Promise<SubscriptionConnection> {
  return adminFetch('/api/admin/subscription-billing/connection/key', {
    method: 'PATCH',
    body: JSON.stringify({ which, value }),
  });
}

// ---------------------------------------------------------------------------
// The price list and pricing a customer at the show (2026-09-24, Task 10a).
//
// Shapes copied from the server, not guessed (adminFetch only casts):
//   AdminPriceList & co   - apps/api/src/services/subscriptionBilling/priceList.ts
//   PriceLine/PricedMonth - apps/api/src/services/subscriptionBilling/pricing.ts
//   NewCustomerPreview    - apps/api/src/services/subscriptionBilling/termService.ts
//   NewCustomerResult     - apps/api/src/services/subscriptionBilling/customerSignup.ts
// Routes: apps/api/src/routes/platformAdmin/index.ts ("Task 9").
// ---------------------------------------------------------------------------

export interface AdminPriceBand {
  id: string;
  minEmployees: number;
  maxEmployees: number | null;
  /** Null for the quoted (101+) band - never priced from the list. */
  monthlyPence: number | null;
  label: string;
  active: boolean;
}
export interface AdminPriceAddon {
  key: string;
  name: string;
  monthlyPence: number;
  /** Empty = every sector may take it. */
  onlyForSectors: string[];
  /** What holding it switches on (ADDON_UNLOCKS); null = billed only. */
  unlocks: string | null;
  active: boolean;
  sort: number;
}
/** Percent off for months, free days, or both (the free days run first). */
export interface AdminPriceOffer {
  code: string;
  percentOff: number | null;
  months: number | null;
  freeDays: number | null;
  onMarketPage: boolean;
  /** The trades it may be used with. Empty = every trade. */
  onlyForSectors: string[];
  /** YYYY-MM-DD */
  lastUseDate: string;
  active: boolean;
}
/** The currency a country's prices are in - its country pack's. *Pence fields are in its smallest unit. */
export interface PriceCurrency {
  code: string;
  symbol: string;
  decimalPlaces: number;
}
/** One country's price list (TODO 529). */
export interface AdminPriceList {
  countryCode: string;
  currency: PriceCurrency;
  bands: AdminPriceBand[];
  addons: AdminPriceAddon[];
  offers: AdminPriceOffer[];
}

export interface PriceLine {
  kind: 'band' | 'addon' | 'deal';
  description: string;
  /** What the customer calls it: "JeanieIQ, team of 11–25", or the add-on's name. */
  name: string;
  pence: number;
  fullPence: number;
}
export interface PricedMonth {
  lines: PriceLine[];
  totalPence: number;
  fullTotalPence: number;
  offer: { code: string; percent: number; monthNumber: number; months: number; savedPence: number } | null;
}
export interface NewCustomerPreview {
  /** Null on an agreed deal. */
  band: { label: string; monthlyPence: number } | null;
  first: PricedMonth;
  /** The first month at full price once the percentage off runs out. Null without one. */
  afterOffer: PricedMonth | null;
  /** The calendar month the price returns to full, e.g. "December 2026". Null without a percentage off. */
  offerEndsLabel: string | null;
  firstIncVatPence: number | null;
  afterOfferIncVatPence: number | null;
  /** Free days before paying (0 = straight to paid), and what the offer itself gives. */
  freeDays: number;
  offerFreeDays: number | null;
  /** The first paid day, YYYY-MM-DD. */
  firstPaidDate: string;
}
export interface PreviewPriceBody {
  /** The customer's country: whose price list. Billing is UK-only for now (TODO 529). */
  countryCode: string;
  sector: string;
  /** Number of staff for a list price; null with an agreed deal. */
  headcount: number | null;
  /** An agreed monthly figure ex VAT, in pounds. */
  dealPounds?: number | null;
  addonKeys: string[];
  offerCode?: string;
  /** Free days typed on the card; null = the offer's own. */
  freeDays?: number | null;
}
export interface CreateCustomerBody extends PreviewPriceBody {
  dealNotes?: string;
  /** Who agreed free days that no offer code gives. */
  freeDaysConfirmedBy?: string;
  name: string;
  slug: string;
  /** One of the country's time zones; left out, the workspace is on the main one (TODO 257). */
  timeZone?: string;
  /** The country's region the company is in (Australia: its state or territory); left out, the country's default. */
  region?: string;
  directorEmail: string;
  directorFirstName: string;
  directorLastName: string;
  directorPhone?: string;
  primarySiteName?: string;
  /** Kept in the workspace's Company Details; the primary site takes the address. */
  company: {
    registeredAddressLine1: string;
    registeredAddressLine2?: string;
    registeredAddressCity: string;
    registeredAddressPostcode: string;
    companyNumber?: string;
    vatNumber?: string;
    companyTelephone?: string;
    accountsEmail?: string;
  };
}
export interface NewCustomerResult {
  tenantId: string;
  tenantSlug: string;
  directorEmail: string;
  /** For staff to pass on ONLY when the welcome did not send (Ruling 16). */
  directorOneTimePassword: string;
  priced: { first: PricedMonth; afterOffer: PricedMonth | null };
  freeDays: number;
  /** YYYY-MM-DD */
  firstPaidDate: string;
  emailSent: boolean;
  emailError?: string;
}

/** One country's price list: its own bands, add-ons, codes and tier prices (TODO 529). */
const pricingPath = (countryCode: string): string => `/api/admin/country/${encodeURIComponent(countryCode)}/subscription-pricing`;
export async function platformAdminGetPriceList(countryCode: string): Promise<AdminPriceList> {
  return adminFetch(pricingPath(countryCode));
}
export async function platformAdminUpdatePriceBand(countryCode: string, id: string, patch: { monthlyPence?: number | null; active?: boolean }): Promise<void> {
  await adminFetch(`${pricingPath(countryCode)}/bands/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) });
}
export interface PriceAddonPatch {
  name?: string;
  monthlyPence?: number;
  active?: boolean;
  onlyForSectors?: string[];
  unlocks?: string | null;
}
/** One place up or down the list. */
export async function platformAdminMovePriceAddon(countryCode: string, key: string, direction: 'up' | 'down'): Promise<void> {
  await adminFetch(`${pricingPath(countryCode)}/addons/${encodeURIComponent(key)}/move`, { method: 'POST', body: JSON.stringify({ direction }) });
}
export async function platformAdminUpdatePriceAddon(countryCode: string, key: string, patch: PriceAddonPatch): Promise<void> {
  await adminFetch(`${pricingPath(countryCode)}/addons/${encodeURIComponent(key)}`, { method: 'PATCH', body: JSON.stringify(patch) });
}
export interface PriceOfferBody {
  percentOff: number | null;
  months: number | null;
  freeDays: number | null;
  onMarketPage: boolean;
  onlyForSectors: string[];
  lastUseDate: string;
  active: boolean;
}
export async function platformAdminUpsertPriceOffer(
  countryCode: string,
  code: string,
  body: PriceOfferBody,
): Promise<void> {
  await adminFetch(`${pricingPath(countryCode)}/offers/${encodeURIComponent(code)}`, { method: 'PUT', body: JSON.stringify(body) });
}

/*
 * ADDING AND REMOVING LINES (TODO 532). A show code is removed outright - its
 * users keep their copy; a band or add-on a customer's term uses is refused by
 * the server (409 IN_USE, saying how many) and is switched off instead.
 */
export interface AddPriceBandBody {
  minEmployees: number;
  /** Null = no top ("101+"). */
  maxEmployees: number | null;
  /** Null = price on request. */
  monthlyPence: number | null;
  active: boolean;
}
export interface AddPriceAddonBody {
  name: string;
  monthlyPence: number;
  onlyForSectors: string[];
  unlocks: string | null;
  active: boolean;
}
export async function platformAdminAddPriceBand(countryCode: string, body: AddPriceBandBody): Promise<{ id: string }> {
  return adminFetch(`${pricingPath(countryCode)}/bands`, { method: 'POST', body: JSON.stringify(body) });
}
export async function platformAdminRemovePriceBand(countryCode: string, id: string): Promise<void> {
  await adminFetch(`${pricingPath(countryCode)}/bands/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export async function platformAdminAddPriceAddon(countryCode: string, body: AddPriceAddonBody): Promise<{ key: string }> {
  return adminFetch(`${pricingPath(countryCode)}/addons`, { method: 'POST', body: JSON.stringify(body) });
}
export async function platformAdminRemovePriceAddon(countryCode: string, key: string): Promise<void> {
  await adminFetch(`${pricingPath(countryCode)}/addons/${encodeURIComponent(key)}`, { method: 'DELETE' });
}
export async function platformAdminRemovePriceOffer(countryCode: string, code: string): Promise<void> {
  await adminFetch(`${pricingPath(countryCode)}/offers/${encodeURIComponent(code)}`, { method: 'DELETE' });
}
export async function platformAdminPreviewPrice(body: PreviewPriceBody): Promise<NewCustomerPreview> {
  return adminFetch('/api/admin/subscription-pricing/preview', { method: 'POST', body: JSON.stringify(body) });
}
export async function platformAdminCreateCustomer(body: CreateCustomerBody): Promise<NewCustomerResult> {
  return adminFetch('/api/admin/customers', { method: 'POST', body: JSON.stringify(body) });
}
/** The add-ons a tenant holds: live at once, ticked in its Configuration, charged from the next month. */
export async function platformAdminSetTenantAddons(tenantId: string, addonKeys: string[]): Promise<{ tenantId: string; addonKeys: string[] }> {
  return adminFetch(`/api/admin/tenants/${encodeURIComponent(tenantId)}/addons`, { method: 'PUT', body: JSON.stringify({ addonKeys }) });
}

/* ── The terms and privacy notice (Full access only at the API) ──────────── */

export type LegalKind = 'terms' | 'privacy';
/** Who the document is written for. Absent on a response from an older API. */
export type LegalAudience = 'customer' | 'locum';

export interface LegalDocumentView {
  id: string;
  kind: LegalKind;
  audience?: LegalAudience;
  version: string;
  body: string;
  publishedAt: string | null;
  /** The one a stranger would be shown right now. */
  live: boolean;
  updatedAt: string;
}

export async function platformAdminListLegalDocuments(): Promise<LegalDocumentView[]> {
  const r = await adminFetch<{ documents: LegalDocumentView[] }>('/api/admin/legal-documents', { method: 'GET' });
  return r.documents;
}

export async function platformAdminSaveLegalDocument(input: {
  kind: LegalKind; audience?: LegalAudience; version: string; body: string;
}): Promise<LegalDocumentView> {
  return adminFetch<LegalDocumentView>('/api/admin/legal-documents', { method: 'POST', body: JSON.stringify(input) });
}

export async function platformAdminPublishLegalDocument(id: string): Promise<LegalDocumentView> {
  return adminFetch<LegalDocumentView>(`/api/admin/legal-documents/${id}/publish`, { method: 'POST' });
}

export async function platformAdminDeleteLegalDraft(id: string): Promise<null> {
  return adminFetch<null>(`/api/admin/legal-documents/${id}`, { method: 'DELETE' });
}

/* ── Locum sign-ups ──────────────────────────────────────────────────────── */

export interface LocumRow {
  id: string;
  name: string;
  email: string;
  /** Self-declared; there is no GPhC register integration. */
  gphcNumber: string | null;
  registration: string;
  active: boolean;
  signedUpAt: string;
  lastLoginAt: string | null;
  /** How many groups hold a record for them. */
  groups: number;
}

export interface LocumList {
  total: number;
  active: number;
  everLoggedIn: number;
  onSomebodysBooks: number;
  locums: LocumRow[];
}

export async function platformAdminListLocums(): Promise<LocumList> {
  return adminFetch<LocumList>('/api/admin/locums', { method: 'GET' });
}
